// Direction A — "Atelier" · responsive home + room views, with photo-derived palette.
// Palette comes from the home: matte joinery black, honey oak, plaster white,
// charcoal stone, dusty linen blue, burnt-amber cushion accent.
//
// Ported from design/new design/direction-a.jsx. Only changes vs. design source:
//  - ESM imports replace window globals
//  - RoomView receives roomState via prop (caller provides mock or HA-bridged)
//  - Pool's missing fields (tv/blinds/louvre/climate) handled via hasX checks
//    already in the component.

import * as React from 'react';
import {
  CoverArt, I, CLIMATE_ICON, CLIMATE_MODE_LABEL,
  useClock, fmtTime, fmtDate, greeting,
} from './shared.jsx';
import { readCurrent, readDays, summarise, asPhrase } from './weather/model.js';
import {
  VACUUM as VAC, areasForPanelRoom, areaName, isRunningSegment,
} from './vacuum/policy.js';

// Palettes and typefaces live in theme.js, named by role. The A_* names
// are kept as the import surface the rest of the panel already uses.
import { LIGHT as A_LIGHT, DARK as A_DARK, FONT as A_FONT, RADIUS } from './theme.js';
import { gestureIntent, levelAt } from './lights/light-drag.js';

// fs — apply Comfort-mode font scaling to body-text sizes. App.jsx
// stamps `scale` onto the active T (1.0 normally, 1.15 when Comfort
// is on). Use at reading-text sites, labels included: since the 2026-10
// redesign labels are 13px and read at a distance, so they scale too.
// Leave display numerals and glyphs raw.
const fs = (T, base) => Math.round(base * (T?.scale || 1));

// Some HA endpoints (notably media_player_proxy serving Sonos cover
// art) return Content-Type: application/octet-stream, which iOS
// Safari refuses to render in <img>. Workaround: fetch the URL,
// rewrap the bytes in a Blob with type image/jpeg, and use the
// resulting object URL as src. Blob URLs respect the blob's
// declared type, so the renderer gets a valid image MIME.
//
// Returns null while loading or on error — callers fall back to
// procedural CoverArt when null.
//
// PWA cross-origin: entity_picture URLs arrive as HA-relative
// (e.g. /api/media_player_proxy/...). In the PWA build (different
// origin from HA) we prepend VITE_HASS_URL so the fetch finds HA.
// In the panel_custom build same-origin works as-is. Absolute URLs
// (Spotify CDN, etc.) pass through unchanged.
const HA_URL_PREFIX = import.meta.env.VITE_HASS_URL || '';
function useImageBlobUrl(srcUrl) {
  const [url, setUrl] = React.useState(null);
  React.useEffect(() => {
    if (!srcUrl) { setUrl(null); return; }
    const fullUrl =
      HA_URL_PREFIX && srcUrl.startsWith('/')
        ? `${HA_URL_PREFIX}${srcUrl}`
        : srcUrl;
    let cancelled = false;
    let createdUrl = null;
    fetch(fullUrl, { credentials: 'same-origin' })
      .then(r => r.ok ? r.blob() : Promise.reject(r.status))
      .then(blob => {
        if (cancelled) return;
        const fixed = new Blob([blob], { type: 'image/jpeg' });
        createdUrl = URL.createObjectURL(fixed);
        setUrl(createdUrl);
      })
      .catch(err => {
        console.warn('[ha-controller] cover art fetch failed', err);
        if (!cancelled) setUrl(null);
      });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [srcUrl]);
  return url;
}

// How long the busy affordance runs before giving up on an AV verb,
// and when its copy degrades from "Turning on…" to "still working".
// Shared by the Screen tile's power toggle and the Activity row so a
// press feels identical whichever control started it.
const AV_INTENT_TIMEOUT = 40000;
const AV_INTENT_SLOW = 15000;

// Lights usually echo from HA in well under a second, so showing a
// spinner the instant one is tapped makes every normal press flicker —
// noise, not reassurance. Hold the busy treatment back this long and
// only genuinely slow or stuck commands ever surface one. (The AV verbs
// don't need this: those chains are never fast.)
const BUSY_REVEAL_DELAY = 400;

// Floor for the AV busy affordance: once a press has started a chain,
// hold the treatment this long even if the settle signal says "done"
// immediately. Without it, a room whose settle read is optimistic
// clears the intent before the first paint and the press lands with no
// affordance at all — see useScreenPower for the Office case that
// prompted this. 1.2 s is long enough to register as an acknowledgement
// and short enough not to feel like lag on a genuinely fast room.
const BUSY_MIN_VISIBLE = 1200;

// True only once `active` has been continuously true for `delay` ms.
// Drops to false immediately when `active` clears, so a light that
// settles at 380 ms never flashes.
function useDelayedFlag(active, delay = BUSY_REVEAL_DELAY) {
  const [shown, setShown] = React.useState(false);
  React.useEffect(() => {
    if (!active) { setShown(false); return; }
    const id = setTimeout(() => setShown(true), delay);
    return () => clearTimeout(id);
  }, [active, delay]);
  return shown;
}

// An Apple TV counts as awake in anything but these. `unknown` belongs
// on the list with `off`: it's HA's no-data state, which pyatv passes
// through for tens of seconds around a wake — precisely the window the
// busy affordance exists to cover. Reading it as "awake" told the tile
// the room was alive mid-transition.
const ATV_ASLEEP = ['off', 'unavailable', 'unknown', null, undefined];
const appleTvAwake = (appleTv) => !!(appleTv && !ATV_ASLEEP.includes(appleTv.state));

// Is the screen itself on? One definition, because there are five
// callers and they were drifting: `tv.on` is the fast echo and, in the
// Sony rooms, an echo is all it is. Where the room maps a real read
// (`tv.real`, from the braviatv entity) that read wins. Everywhere else
// — Pavilion's webostv, and the seeded mock rooms — `real` is null and
// this collapses to `tv.on` exactly as before.
export const tvIsOn = (tv) => (typeof tv?.real === 'boolean' ? tv.real : !!tv?.on);

// The two reads of "is this room's screen awake", split out pure so the
// distinction is testable — see useScreenPower for why they differ and
// src/dev/screen-power.test.mjs for the cases.
//
//   awake   — display truth: drives the lit treatment and the direction
//             of the next press.
//   settled — settle truth: answers "has the chain finished?" only.
//
// An assumed_state TV can report the first and not the second, because
// the integration writes the state it asked for. Fall through to the
// Apple TV there; with no Apple TV mapped there's nothing better to
// read, so the two collapse back together rather than never settling.
export function screenReads(room) {
  const tv = room?.tv;
  const atvAwake = appleTvAwake(room?.appleTv);
  // `tv.real` is the honest read (the braviatv entity in the Sony rooms,
  // null everywhere else). Prefer it: `tv.on` is an echo of what HA asked
  // for in those rooms, and it can hold `on` after the TV has been
  // switched off at the Sony remote. That is what left this surface and
  // the remote disagreeing inside a session — the panel believed the room
  // was awake, so Power sent `off` when Ben wanted `on`, and the room
  // never came up. Reported and replicated 2026-09-27.
  //
  // Where the TV entity itself is honest (not assumed — Pavilion's
  // webostv), it is the whole answer and the Apple TV is not consulted.
  // The Apple TV plays through the TV there, so it cannot be "on" with
  // the screen off, and its session can go stale: on 2026-10-26 it held
  // `playing` for 14 h while asleep, so the room read awake and every
  // Power press sent pavilion_off, whose Apple TV turn_off woke the
  // stack over CEC. The Sony rooms keep the OR — their TV entity is
  // assumed, and the Apple TV is their fastest real read.
  const awake = tv && !tv.assumed
    ? tvIsOn(tv)
    : !!(tvIsOn(tv) || atvAwake);
  return {
    awake,
    settled: tv?.assumed && room?.appleTv ? atvAwake : awake,
  };
}

// ── useScreenPower ──────────────────────────────────────────────────
//
// The whole-stack power intent for a room's screen. Shared by the room
// tile's Screen power button and the TV takeover's Power button so a
// press behaves identically wherever it's made — before this, only the
// tile had any in-flight affordance and the remote's Power had a 120 ms
// ink flash.
//
// Two different reads of "is this room awake", and conflating them is
// what broke the Office:
//
//   `awake`   — display truth. Drives the lit treatment and the
//               on/off direction of the next press.
//   `settled` — settle truth. Answers "has the chain finished?" and
//               nothing else.
//
// They diverge on assumed_state entities. Office's TV is an
// androidtv_remote, so HA writes `on` in the same event as the service
// call: the state went on at 22:40:35.720 while script.office_watch
// triggered at 22:40:35.723. Settling on that is settling on "the call
// went out", so the intent cleared before React painted the press and
// the ring never mounted — no affordance at all, in that room only.
//
// This block used to add "Pavilion (webostv) and Rumpus (braviatv) sit
// `unavailable` until the panel genuinely lights, so the identical code
// worked there". Half of that was wrong and it cost a second bug.
// `media_player.rumpus_tv` is androidtv_remote, not braviatv — the
// braviatv entity is `media_player.rumpus_tv_2` — and it rests at `off`,
// not `unavailable`. Rumpus is in Office's class, not Pavilion's. Only
// Pavilion is genuinely honest here. Verified against live state and
// seven days of history 2026-09-27; the same false premise had
// script.rumpus_watch skipping its own WoL step, so the room stopped
// powering on at all.
//
// Where the TV is assumed and an Apple TV is mapped, settle on the
// Apple TV, which reports for real. Everywhere else the two reads are
// the same value. BUSY_MIN_VISIBLE then floors the affordance so even
// a room that settles honestly-but-instantly still acknowledges.
// `awake` no longer reads the assumed entity at all where a real one is
// mapped — see screenReads.
export function useScreenPower({ room, act, update }) {
  const tv = room?.tv;
  const { awake, settled } = screenReads(room);

  const [intent, setIntent] = React.useState(null);   // null | 'on' | 'off'
  const [intentAt, setIntentAt] = React.useState(0);  // epoch ms of the press
  const abandonTimer = React.useRef(null);
  const clear = React.useCallback(() => {
    setIntent(null);
    if (abandonTimer.current) { clearTimeout(abandonTimer.current); abandonTimer.current = null; }
  }, []);

  // Clear on settle, but never before the floor has elapsed.
  React.useEffect(() => {
    if (intent == null) return;
    if (intent === 'on' ? !settled : settled) return;
    const left = BUSY_MIN_VISIBLE - (Date.now() - intentAt);
    if (left <= 0) { clear(); return; }
    const id = setTimeout(clear, left);
    return () => clearTimeout(id);
  }, [intent, intentAt, settled, clear]);
  React.useEffect(() => () => { if (abandonTimer.current) clearTimeout(abandonTimer.current); }, []);

  // The intent IS the busy state now that clearing is floored — no need
  // to re-derive it from intent-vs-actual, which is exactly the
  // comparison that failed on an assumed_state entity.
  const busy = intent != null;

  // Local 1 s tick while in flight — drives the "still working" copy
  // without depending on an HA state push to re-render.
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    if (!busy) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [busy]);
  const slow = busy && intentAt > 0 && Date.now() - intentAt > AV_INTENT_SLOW;

  const press = React.useCallback(() => {
    const target = awake ? 'off' : 'on';
    setIntent(target);
    setIntentAt(Date.now());
    setTick(0);
    if (abandonTimer.current) clearTimeout(abandonTimer.current);
    abandonTimer.current = setTimeout(() => setIntent(null), AV_INTENT_TIMEOUT);
    if (room?.av) act?.(room.id, target === 'on' ? 'watch' : 'off');
    else if (room?.id) update?.(room.id, { tv: { ...tv, on: target === 'on' } });
  }, [awake, room, tv, act, update]);

  const label = busy
    ? (intent === 'on' ? 'Turning on' : 'Turning off')
    : (awake ? 'Turn off' : 'Turn on');

  return { awake, settled, busy, intent, slow, press, label };
}

const ALabel = ({ children, T, icon, style }) => (
  <div style={{
    fontFamily: A_FONT.sans, fontSize: fs(T, 13), letterSpacing: '.12em',
    textTransform: 'uppercase', color: T.inkSoft, fontWeight: 500,
    display: 'inline-flex', alignItems: 'center', gap: 6, ...style,
  }}>
    {icon && (
      <span style={{ display: 'inline-flex', color: T.mute, transform: 'translateY(1px)' }}>
        {React.cloneElement(icon, { size: 12, sw: 1.5 })}
      </span>
    )}
    {children}
  </div>
);

// Tile shell — inline controls live in children; the expand pill at
// the bottom opens the modal (or a full-takeover surface for Sonos).
// `expandLabel` is context-aware per call site (Remote / Music / All
// lights / All shades / Adjust) so the affordance describes the
// destination, not a generic "open."
function ATile({ T, label, icon, onExpand, expandLabel = 'Open', headerAction, onTileClick, span = 1, children, style }) {
  // `onTileClick` makes the whole card a tap target (cursor pointer +
  // click handler). Interactive children must call stopPropagation in
  // their own onClick or they'll double-fire (the button action AND
  // the tile click). Per user feedback 2026-05-24 the screen and
  // sonos tiles wanted whole-tile-clickability for "navigate to
  // detail view" on tap — adopted here as an opt-in prop so tiles
  // that have lots of embedded controls (lights chips, climate
  // +/-) don't accidentally inherit the same behaviour.
  const clickable = !!onTileClick;
  return (
    <div
      onClick={clickable ? onTileClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTileClick(); }
      } : undefined}
      style={{
        gridColumn: `span ${span}`,
        background: T.card, borderRadius: RADIUS.card, boxShadow: T.cardShadow,
        padding: 20, color: T.ink, fontFamily: A_FONT.sans,
        display: 'flex', flexDirection: 'column', minHeight: 0, position: 'relative',
        // overflow: hidden so a tile body that exceeds its container
        // (e.g. an oversized cover whose aspect-ratio didn't propagate
        // to the grid row on older iPad Safari) gets clipped at the
        // tile edge instead of bleeding into the tile below. See iPad
        // rendering bug 2026-05-25.
        overflow: 'hidden',
        cursor: clickable ? 'pointer' : 'default',
        ...style,
      }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44 }}>
        {/* `icon` is accepted but no longer drawn: the 2026-10 labels are
            larger, and the glyph beside them was decoration that cost the
            width a header action needs. */}
        <ALabel T={T} style={{ minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', display: 'block' }}>{label}</ALabel>
        {headerAction && (
          <div style={{ marginLeft: 'auto', display: 'flex' }}
               onClick={(e) => e.stopPropagation()}>
            {headerAction}
          </div>
        )}
      </div>
      <div style={{ flex: 1, minHeight: 0, marginTop: 10, display: 'flex', flexDirection: 'column' }}>
        {children}
      </div>
      {/* Hide the explicit expand button when the whole tile is
          clickable — it's redundant, and the user explicitly asked
          for it gone (2026-05-24). onExpand is still respected as
          the action target for any caller that wires onExpand
          without onTileClick. */}
      {onExpand && !onTileClick && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <button onClick={(e) => { e.stopPropagation(); onExpand(); }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '7px 14px', borderRadius: 999,
              border: `1px solid ${T.hair}`, background: 'transparent',
              color: T.inkSoft, cursor: 'pointer',
              fontFamily: A_FONT.sans, fontSize: 11, fontWeight: 500,
              letterSpacing: '.14em', textTransform: 'uppercase',
              transition: 'border-color .15s, color .15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = T.selected;
              e.currentTarget.style.color = T.ink;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = T.hair;
              e.currentTarget.style.color = T.inkSoft;
            }}>
            {expandLabel}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 6l6 6-6 6"/>
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

// Modal shell — backdrop + centered card. ESC + backdrop click close.
function AClimateModal({ T, room, update }) {
  // `room.climate.pending` is the hook's intent layer reporting that HA
  // hasn't yet confirmed the setpoint or mode. The target number already
  // shows the intended value, so the spinner is only saying "not
  // acknowledged yet" — same delayed reveal as lights, since a
  // responsive thermostat shouldn't make the UI twitch.
  const busy = useDelayedFlag(!!room.climate?.pending);
  // Mode colour on the reading and its glyph (user direction 2026-05-25:
  // the icon sits by the temperature and shares its colour). Since the
  // 2026-10 redesign heating is ink and cooling is linen; nothing is
  // orange.
  const modeTint = room.mode === 'heat' ? T.heat
                 : room.mode === 'cool' ? T.cool
                 : room.mode === 'off'  ? T.mute
                 : T.ink;
  const modeIcon = CLIMATE_ICON[room.mode] || I.thermo;
  const heroIcon = React.cloneElement(modeIcon, { style: { width: 36, height: 36 } });
  const nudge = (d) => update(room.id, { target: Math.min(28, Math.max(16, room.target + d)) });
  const round = (label, glyph, onClick) => (
    <button onClick={onClick} aria-label={label}
      style={{ width: 52, height: 52, borderRadius: '50%', border: 'none', background: T.cardSoft, color: T.ink, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {glyph}
    </button>
  );
  return (
    <Card T={T}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <span style={{ color: modeTint, display: 'flex', transition: 'color .25s ease' }}>{heroIcon}</span>
        <span style={{ fontFamily: A_FONT.serif, fontSize: 80, lineHeight: .95, fontWeight: 500, color: modeTint, fontVariantNumeric: 'lining-nums tabular-nums', transition: 'color .25s ease' }}>
          {room.temp.toFixed(1)}
          <span style={{ fontFamily: A_FONT.sans, fontWeight: 400, fontSize: 30, verticalAlign: 34, marginLeft: -2 }}>°</span>
        </span>
        <span style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 14), color: T.inkSoft }}>
          Inside · {room.humidity}% humidity
        </span>
      </div>
      {/* Target — the big number flanked by steppers is the primary
          control (every physical thermostat works this way). Beneath it a
          scale of the 16-28° range at 0.5° steps: each step is a full-height
          tap target with a small mark, so it can be hit with a finger. */}
      <div>
        <ALabel T={T} style={{ marginBottom: 8 }}>
          Target
          {busy && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 8, color: T.busy, textTransform: 'none', letterSpacing: 0 }}>
              <BusyDot T={T} size={12}/>
              Sending
            </span>
          )}
        </ALabel>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, justifyContent: 'center', padding: '6px 0' }}>
          {round('Decrease target by 0.5°', I.minus, () => nudge(-0.5))}
          <div style={{ minWidth: 150, textAlign: 'center', fontFamily: A_FONT.serif, fontSize: 56, color: T.ink, fontWeight: 500, lineHeight: 1, fontVariantNumeric: 'lining-nums tabular-nums' }}>
            {room.target.toFixed(1)}
            <span style={{ fontFamily: A_FONT.sans, fontWeight: 400, fontSize: 22, verticalAlign: 24, marginLeft: -2 }}>°</span>
          </div>
          {round('Increase target by 0.5°', I.plus, () => nudge(0.5))}
        </div>
        <div style={{ display: 'flex', alignItems: 'stretch', maxWidth: 520, margin: '8px auto 0' }}>
          {Array.from({ length: 25 }, (_, i) => 16 + i * 0.5).map((deg) => {
            const isTarget = Math.abs(deg - room.target) < 0.001;
            return (
              <button key={deg}
                onClick={() => update(room.id, { target: deg })}
                aria-label={`Set target to ${deg.toFixed(1)}°`}
                style={{ flex: 1, minWidth: 0, height: 40, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{
                  width: isTarget ? 12 : 5, height: isTarget ? 12 : 5, borderRadius: '50%',
                  background: isTarget ? T.selected : T.hairHi,
                  transition: 'width .15s ease, height .15s ease, background .15s ease',
                }}/>
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', maxWidth: 520, margin: '0 auto', fontFamily: A_FONT.sans, fontSize: fs(T, 12), color: T.inkSoft, fontVariantNumeric: 'tabular-nums' }}>
          <span>16°</span>
          <span>28°</span>
        </div>
      </div>
      <div>
        <ALabel T={T} style={{ marginBottom: 10 }}>Mode</ALabel>
        {/* `heat_cool`, not `auto` — every unit here reports
            hvac_modes ["heat_cool","off","fan_only","dry","heat","cool"]
            and none accepts `auto`, so the old chip sent a mode HA
            rejects and silently did nothing. Verified across all six
            climate entities 2026-07-19. Only the option being moved *to*
            shows the spinner: the chosen one is already the intended mode. */}
        <Segmented T={T} ariaLabel="Mode" height={52}
          options={['heat_cool', 'heat', 'cool', 'off'].map((m) => ({
            value: m,
            label: CLIMATE_MODE_LABEL[m] || m,
            icon: <span style={{ display: 'flex' }}>{React.cloneElement(CLIMATE_ICON[m] || I.thermo, { style: { width: 18, height: 18 } })}</span>,
          }))}
          value={room.mode} busyValue={busy ? room.mode : null}
          onChange={(m) => update(room.id, { mode: m })}/>
      </div>
    </Card>
  );
}

// LightRow — one fixture in the lights takeover: the same LightKey as
// the room tile (tap to switch, drag a lit key to dim), with the exact-
// level controls beneath it for dimmable fixtures: a ±10% stepper and an
// 11-dot scale. The dots are drawn small but each sits in a full-height
// tap target, so the scale can be hit with a finger rather than a nail.
// The family rejected a thin slider in May 2026 for exactly that.
//
// `l.pending` comes from the hook's intent layer: true while HA hasn't
// yet confirmed what was asked for. The key already shows the intended
// state, so its spinner answers "still working on it", not "what state
// am I in" — which is why it waits out BUSY_REVEAL_DELAY.
function LightRow({ T, l, zoned, toggleLight, stepBrightness, jumpBrightness }) {
  const dimmable = l.brightness != null;
  const displayName = zoned
    ? (l.name.includes(' · ') ? l.name.split(' · ').slice(1).join(' · ') : l.name)
    : l.name;
  // The dot nearest the level, so 71% marks 70 rather than marking nothing.
  const nearest = l.on && dimmable ? Math.round(l.brightness / 10) * 10 : null;
  const stepBtn = (delta, glyph, label) => (
    <button onClick={() => stepBrightness(l, delta)} aria-label={label}
      style={{
        width: 44, height: 44, borderRadius: '50%', border: 'none', flexShrink: 0,
        background: T.cardSoft, color: T.ink, cursor: 'pointer', fontSize: 18,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>{glyph}</button>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <LightKey T={T} name={displayName} label={l.name}
        on={l.on} level={dimmable ? l.brightness : null} pending={l.pending}
        onToggle={() => toggleLight(l)}
        onSetLevel={dimmable ? (v) => jumpBrightness(l, v) : undefined}/>
      {dimmable && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {stepBtn(-10, '−', `Decrease ${l.name} brightness`)}
          <div style={{ flex: 1, display: 'flex', alignItems: 'stretch' }}>
            {Array.from({ length: 11 }, (_, i) => i * 10).map((pct) => {
              const isAt = nearest === pct;
              return (
                <button key={pct}
                  onClick={() => jumpBrightness(l, pct)}
                  aria-label={`Set ${l.name} to ${pct}%`}
                  style={{
                    flex: 1, height: 44, minWidth: 0, padding: 0, border: 'none',
                    background: 'transparent', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                  <span style={{
                    width: isAt ? 12 : 6, height: isAt ? 12 : 6, borderRadius: '50%',
                    background: isAt ? T.selected : T.hairHi,
                    transition: 'width .15s ease, height .15s ease, background .15s ease',
                  }}/>
                </button>
              );
            })}
          </div>
          {stepBtn(10, '+', `Increase ${l.name} brightness`)}
        </div>
      )}
    </div>
  );
}

function ALightsModal({ T, room, updateLight, updateUtility }) {
  // Filter out lights flagged unavailable (Z-Wave dropouts, offline
  // entities) — same rule as the tile. They're not actionable, and
  // showing them as permanently-off rows with a non-responsive slider
  // is worse than hiding them.
  const lights = room.lights.filter(l => l.available !== false);
  const allOn  = lights.length > 0 && lights.every(l => l.on);

  // If any light carries a `zone:` field, group by zone with subheads
  // — Pavilion's open-plan area uses this to separate Kitchen / Living
  // / Dining fixtures. Rooms without zones render as a single flat
  // section (the historical layout).
  const zoned = lights.some(l => l.zone);
  const ZONE_LABELS = { kitchen: 'Kitchen', living: 'Living', dining: 'Dining', bedroom: 'Bedroom', wardrobe: 'Wardrobe' };
  const ZONE_ORDER  = ['kitchen', 'living', 'dining', 'bedroom', 'wardrobe'];
  const groups = zoned
    ? ZONE_ORDER
        .map(z => ({ zone: z, label: ZONE_LABELS[z] || z, lights: lights.filter(l => l.zone === z) }))
        .filter(g => g.lights.length > 0)
        .concat(
          // Catch-all bucket for any unzoned lights that slipped through.
          (() => {
            const stray = lights.filter(l => !l.zone || !ZONE_ORDER.includes(l.zone));
            return stray.length ? [{ zone: null, label: 'Other', lights: stray }] : [];
          })()
        )
    : [{ zone: null, label: null, lights }];

  // ── Light-row helpers ────────────────────────────────────────────
  //
  // Per user feedback 2026-05-25: the old amber-circle + 3px slider
  // tested poorly (small target, no precision). Replaced with a
  // bulb-glyph on/off control + climate-style ±10% stepper + dotted
  // scale beneath. Same visual vocabulary as AClimateModal's target-
  // temp picker so both shells feel consistent.
  const stepBrightness = (l, delta) => {
    const next = Math.max(0, Math.min(100, (l.brightness ?? 0) + delta));
    updateLight(room.id, l.id, { brightness: next, on: next > 0 });
  };
  const jumpBrightness = (l, value) => {
    updateLight(room.id, l.id, { brightness: value, on: value > 0 });
  };
  const toggleLight = (l) => {
    updateLight(room.id, l.id, {
      on: !l.on,
      // Lit-without-level recovery: if a light is off at 0%, taking it
      // on at 0% would look broken. Default to 50% in that case.
      brightness: !l.on && l.brightness === 0 ? 50 : l.brightness,
    });
  };

  const renderLightRow = (l) => (
    <LightRow key={l.id} T={T} l={l} zoned={zoned}
      toggleLight={toggleLight} stepBrightness={stepBrightness} jumpBrightness={jumpBrightness}/>
  );

  // Per-zone master toggle — "any on" semantics for the active state,
  // and tap-to-toggle either turns the whole zone on (lights at 60%
  // if previously 0) or fully off. Mirrors the room-level All Lights
  // toggle scoped to a single zone.
  const toggleZone = (zoneLights) => {
    const anyOn = zoneLights.some((l) => l.on);
    const targetOn = !anyOn;
    zoneLights.forEach((l) => updateLight(room.id, l.id, {
      on: targetOn,
      brightness: targetOn && l.brightness === 0 ? 60 : l.brightness,
    }));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card T={T} eyebrow="All lights">
        {/* Two plain actions rather than a switch: a switch has to show a
            state, and "all on" is true of a room almost never. */}
        <div style={{ display: 'flex', gap: 8 }}>
          {[
            { on: true,  label: 'All on',  disabled: allOn },
            { on: false, label: 'All off', disabled: !lights.some((l) => l.on) },
          ].map(({ on, label, disabled }) => (
            <button key={label} disabled={disabled}
              onClick={() => lights.forEach((l) => updateLight(room.id, l.id, { on, brightness: on && l.brightness === 0 ? 60 : l.brightness }))}
              style={{
                flex: 1, height: 48, border: 'none', borderRadius: RADIUS.control,
                background: T.cardSoft, color: disabled ? T.mute : T.ink,
                fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
                cursor: disabled ? 'default' : 'pointer',
              }}>{label}</button>
          ))}
        </div>
      </Card>
      {groups.map((g) => {
        const zoneOn = g.lights.some((l) => l.on);
        return (
          <Card key={g.zone || 'all'}
            T={T}
            eyebrow={g.label || 'Lights'}
            headerAction={
              <ZonePowerButton T={T} on={zoneOn} lights={g.lights}
                onClick={() => toggleZone(g.lights)}
                ariaLabel={zoneOn ? `Turn off ${g.label || 'lights'}` : `Turn on ${g.label || 'lights'}`}/>
            }>
            {g.lights.map(renderLightRow)}
          </Card>
        );
      })}
      {/* Utilities — switched non-fixture loads (exhaust fans, heated
          towel rails). The same key as a light, with no level and no
          stepper. Master
          PowerButton only when 2+ utilities are present; with a
          single utility the row is its own affordance. See
          [[project-fibaro-switched-loads-as-light]] for the domain
          quirk + [[project-fibaro-entity-naming-schema]] for the IDs. */}
      {(() => {
        const utils = (room.utilities || []).filter((u) => u.available !== false);
        if (utils.length === 0) return null;
        const anyOn = utils.some((u) => u.on);
        const toggleAll = () => {
          const targetOn = !anyOn;
          utils.forEach((u) => updateUtility?.(room.id, u.id, { on: targetOn }));
        };
        return (
          <Card T={T} eyebrow="Utilities"
            headerAction={utils.length > 1
              ? <ZonePowerButton T={T} on={anyOn} lights={utils} onClick={toggleAll}
                  ariaLabel={anyOn ? 'Turn off all utilities' : 'Turn on all utilities'}/>
              : null}>
            {utils.map((u) => (
              <LightKey key={u.id} T={T} name={u.name} label={u.name}
                on={u.on} level={null} pending={u.pending}
                onToggle={() => updateUtility?.(room.id, u.id, { on: !u.on })}/>
            ))}
          </Card>
        );
      })()}
    </div>
  );
}

// LightKey — one fixture (or utility) on the room tile's Lighting card.
// The key IS the state: white and resting when off, oak and lifted when
// on, so there is no glyph or outline to read across a kitchen. A tap
// anywhere on an off key, or on the name side of a lit one, switches it.
// A lit key carries a deeper-oak cell on the right holding its level;
// that cell is a separate target that opens the lights takeover for an
// exact level (`onLevel`), sized at 56px so a wet near-miss lands on
// one target or the other rather than between them. Fixtures with no
// level (switch-backed lights, fans, towel rails) show "On" there.
//
// `pending` is the hook's intent layer, as for LightRow: the key already
// shows the intended state, and after BUSY_REVEAL_DELAY a spinner takes
// the level's place to say "not confirmed yet".
//
// Drag-to-dim (`onSetLevel`, dimmable lit keys only): a sideways drag
// across the key sets the level, snapping to 10%. The oak fill shows the
// level only while the finger is down and the cell counts with it; one
// call goes out on release. The rules (commit distance, scroll vs drag,
// snapping, the 10% floor) live in lights/light-drag.js with a test. A
// press that turns into a drag never also toggles the light, and a
// vertical swipe is left to the page so a phone can scroll past keys.
function LightKey({ T, name, label, on, level, pending, onToggle, onLevel, onSetLevel }) {
  const busy = useDelayedFlag(!!pending);
  const boxRef = React.useRef(null);
  const press = React.useRef(null);         // { x, y, id, mode: null | 'drag' | 'scroll' }
  const dragLevel = React.useRef(null);
  const swallowClick = React.useRef(false);
  const [drag, setDrag] = React.useState(null);   // live level while dragging, else null
  const dimmable = on && level != null && !!onSetLevel;

  const text = {
    fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  };
  const reset = { border: 'none', margin: 0, cursor: 'pointer', textAlign: 'left' };
  if (!on) {
    return (
      <button onClick={onToggle} aria-label={`${label}, off. Turn on`} aria-busy={busy || undefined}
        style={{
          ...reset, width: '100%', height: 44, padding: '0 14px',
          borderRadius: RADIUS.key, background: T.keyFill, color: T.ink,
          boxShadow: T.keyShadow,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          transition: 'background .18s ease-out, box-shadow .18s ease-out',
        }}>
        <span style={text}>{name}</span>
        {busy && <BusyDot T={T} size={16} color={T.ink}/>}
      </button>
    );
  }

  const levelFor = (clientX) => {
    const r = boxRef.current.getBoundingClientRect();
    return levelAt(clientX, r.left, r.width);
  };
  const onPointerDown = (e) => {
    if (!dimmable || (e.pointerType === 'mouse' && e.button !== 0)) return;
    press.current = { x: e.clientX, y: e.clientY, id: e.pointerId, mode: null };
  };
  const onPointerMove = (e) => {
    const p = press.current;
    if (!p || p.id !== e.pointerId || p.mode === 'scroll') return;
    if (p.mode == null) {
      const intent = gestureIntent(e.clientX - p.x, e.clientY - p.y);
      if (intent === 'scroll') { press.current = null; return; }
      if (intent !== 'drag') return;
      p.mode = 'drag';
      boxRef.current.setPointerCapture?.(e.pointerId);
    }
    const next = levelFor(e.clientX);
    dragLevel.current = next;
    setDrag(next);
  };
  const endPress = (commit) => (e) => {
    const p = press.current;
    press.current = null;
    if (!p || p.id !== e.pointerId || p.mode !== 'drag') return;
    swallowClick.current = true;            // the click that follows a drag is not a tap
    if (commit && dragLevel.current != null) onSetLevel(dragLevel.current);
    dragLevel.current = null;
    setDrag(null);
  };
  const onClickCapture = (e) => {
    if (!swallowClick.current) return;
    swallowClick.current = false;
    e.stopPropagation();
    e.preventDefault();
  };

  const dragging = drag != null;
  return (
    <div ref={boxRef} aria-busy={busy || undefined}
      data-dragging={dragging || undefined}
      onPointerDown={onPointerDown} onPointerMove={onPointerMove}
      onPointerUp={endPress(true)} onPointerCancel={endPress(false)}
      onClickCapture={onClickCapture}
      style={{
        position: 'relative', display: 'flex', height: 44,
        borderRadius: RADIUS.key, overflow: 'hidden',
        background: dragging ? T.keyFill : T.litFill, boxShadow: T.litShadow,
        // Vertical swipes stay with the page; sideways moves come here.
        touchAction: dimmable ? 'pan-y' : 'auto', userSelect: 'none',
        transition: 'background .18s ease-out, box-shadow .18s ease-out',
      }}>
      {dragging && (
        <span aria-hidden style={{
          position: 'absolute', top: 0, bottom: 0, left: 0, width: `${drag}%`,
          background: T.litFill, transition: 'width 60ms linear',
        }}/>
      )}
      <button onClick={onToggle} aria-pressed="true" aria-label={`${label}, on. Turn off`}
        style={{
          ...reset, position: 'relative', flex: 1, minWidth: 0, height: '100%', padding: '0 0 0 14px',
          background: 'transparent', color: T.litFillInk, ...text,
        }}>
        {name}
      </button>
      <button onClick={onLevel} disabled={!onLevel}
        aria-label={level != null ? `${label} at ${level} percent. Set level` : `${label} is on`}
        style={{
          ...reset, position: 'relative', width: 56, flexShrink: 0, height: '100%', padding: 0,
          cursor: onLevel ? 'pointer' : 'default',
          background: dragging ? 'transparent' : T.litCell, color: T.litFillInk,
          fontFamily: A_FONT.sans, fontSize: fs(T, 14), fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
        {dragging
          ? `${drag}%`
          : busy
            ? <BusyDot T={T} size={16} color={T.litFillInk}/>
            : (level != null ? `${level}%` : 'On')}
      </button>
    </div>
  );
}

// ZonePowerButton — a master toggle over a group of lights/utilities.
// Busy while *any* member is still unconfirmed, since a zone flip fires
// one service call per member and they land at different times; the
// group isn't settled until the slowest one is.
function ZonePowerButton({ T, on, lights, onClick, ariaLabel }) {
  const busy = useDelayedFlag(lights.some((l) => l.pending));
  return <PowerButton T={T} on={on} busy={busy} onClick={onClick} ariaLabel={ariaLabel} tone="light"/>;
}

// Segmented — a quiet tray of mutually exclusive options. The chosen
// one takes the selected fill (joinery black); with no known value none
// is drawn chosen rather than guessing. `busyValue` marks the option a
// press is moving to, with a spinner in place of the fill.
function Segmented({ T, options, value, onChange, busyValue, ariaLabel, height = 42, fontSize = 15, stretch = true }) {
  return (
    <div role="group" aria-label={ariaLabel} style={{
      display: 'flex', padding: 3, borderRadius: RADIUS.control, background: T.cardSoft,
      flex: stretch ? 1 : '0 0 auto',
    }}>
      {options.map((o) => {
        const chosen = value === o.value;
        const busy = busyValue === o.value;
        return (
          <button key={o.value} onClick={() => onChange(o.value)}
            aria-pressed={chosen} aria-busy={busy || undefined}
            style={{
              flex: 1, minWidth: 0, height, padding: '0 12px',
              border: 'none', borderRadius: RADIUS.key, cursor: 'pointer',
              background: chosen ? T.selected : 'transparent',
              color: chosen ? T.selectedInk : T.ink,
              fontFamily: A_FONT.sans, fontSize: fs(T, fontSize), fontWeight: 500,
              whiteSpace: 'nowrap',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              transition: 'background .18s ease-out, color .18s ease-out',
            }}>
            {busy && <BusyDot T={T} size={14} color={chosen ? T.selectedInk : T.ink}/>}
            {o.icon}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// Louvres list — used by both the Shades tile (compact) and the
// Shades takeover (larger). Each louvre is a row: its name, then an
// Open / Closed tray. Louvres without a state surface in HA (High, West,
// Rumpus) read `unknown`, so neither side is drawn chosen until pressed.
function LouvresList({ T, louvres, louvreCommand, label, size = 'compact' }) {
  // State now lives in HA (input_boolean.*_louvre_open) — read via
  // the overlay's `state` field, write via louvreCommand which fires
  // both the Fibaro pulse switch and the matching input_boolean
  // turn_on/off so all devices share the same Open/Closed picture.
  const dispatch = (lv, action) => {
    louvreCommand?.({
      switch: action === 'open' ? lv.openSwitch : lv.closeSwitch,
      stateBoolean: lv.stateBoolean,
      target: action,
    });
  };
  return (
    <div>
      {label && <ALabel T={T} style={{ marginBottom: 10 }}>{label}</ALabel>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {louvres.map((lv) => (
          <div key={lv.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500, color: T.ink, minWidth: 48, flexShrink: 0 }}>
              {lv.name}
            </span>
            <Segmented T={T} ariaLabel={`${lv.name} louvres`}
              height={size === 'compact' ? 40 : 44}
              options={[{ value: 'open', label: 'Open' }, { value: 'closed', label: 'Closed' }]}
              value={lv.state} onChange={(v) => dispatch(lv, v)}/>
          </div>
        ))}
      </div>
    </div>
  );
}

function AShadesModal({ T, room, update, louvreCommand, coverCommand }) {
  // Each cover type (blinds, sheers) is its own card; louvres get a card
  // at the end. `moving` is the cover's own opening/closing state from
  // HA, not a guess — see overlayCoverMoving. Blinds travel 20-60 s, so
  // this is the surface where an in-flight signal matters most, and it
  // stays lit for automation-driven movement (sun-tracking) as well as
  // taps.
  const coverRows = [
    room.blinds != null && { k: 'blinds', label: 'Blinds', val: room.blinds, moving: room.blindsMoving },
    room.sheer  != null && { k: 'sheer',  label: 'Sheers', val: room.sheer,  moving: room.sheerMoving },
  ].filter(Boolean);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {coverRows.map((x) => (
        <Card key={x.k} T={T}
          eyebrow={x.moving ? `${x.label} · ${x.moving === 'opening' ? 'Opening' : 'Closing'}` : x.label}
          headerAction={
            <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
              {x.moving && <span style={{ alignSelf: 'center', display: 'flex' }}><BusyDot T={T} size={16}/></span>}
              <span style={{ fontFamily: A_FONT.serif, fontSize: 40, fontWeight: 500, lineHeight: 1, color: T.ink, fontVariantNumeric: 'lining-nums tabular-nums' }}>{x.val}%</span>
              <span style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.inkSoft }}>open</span>
            </span>
          }>
          {/* Position scale — 11 steps at 0/10/.../100%, each a full-height
              tap target. A tap fires cover.set_cover_position once (the
              old drag slider fired it continuously and confused the motor). */}
          <div>
            <div style={{ display: 'flex', alignItems: 'stretch' }}>
              {Array.from({ length: 11 }, (_, i) => i * 10).map((v) => {
                const isAtPos = Math.abs(v - x.val) < 5;
                return (
                  <button key={v}
                    onClick={() => update(room.id, { [x.k]: v })}
                    aria-label={`Set to ${v}%`}
                    style={{ flex: 1, minWidth: 0, height: 44, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{
                      width: isAtPos ? 12 : 6, height: isAtPos ? 12 : 6, borderRadius: '50%',
                      background: isAtPos ? T.selected : T.hairHi,
                      transition: 'width .15s ease, height .15s ease, background .15s ease',
                    }}/>
                  </button>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: A_FONT.sans, fontSize: fs(T, 12), color: T.inkSoft }}>
              <span>Closed</span>
              <span>Open</span>
            </div>
          </div>
          {/* Open / Close / Stop — full-throw motion. While the cover
              travels, its direction shows the spinner and Stop lifts out
              as a key: during a 20-60 s throw the useful action is almost
              always "stop it". */}
          <div style={{ display: 'flex', gap: 8 }}>
            {['open', 'close', 'stop'].map((action) => {
              const isTravelDirection =
                (action === 'open' && x.moving === 'opening') ||
                (action === 'close' && x.moving === 'closing');
              const emphasise = isTravelDirection || (action === 'stop' && !!x.moving);
              return (
                <button key={action} onClick={() => coverCommand?.(room.id, x.k, action)}
                  aria-busy={isTravelDirection || undefined}
                  style={{
                    flex: 1, height: 52, border: 'none', borderRadius: RADIUS.control,
                    background: emphasise ? T.keyFill : T.cardSoft,
                    boxShadow: emphasise ? T.keyShadow : 'none',
                    color: T.ink, cursor: 'pointer',
                    fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    transition: 'background .2s, box-shadow .2s',
                  }}>
                  {isTravelDirection && <BusyDot T={T} size={14}/>}
                  {action === 'open' ? 'Open' : action === 'close' ? 'Close' : 'Stop'}
                </button>
              );
            })}
          </div>
        </Card>
      ))}
      {room.louvres && room.louvres.length > 0 && (
        <Card T={T} eyebrow="Louvres">
          <LouvresList T={T} louvres={room.louvres} louvreCommand={louvreCommand}
            label={null} size="modal"/>
        </Card>
      )}
      {/* Sun-tracking day toggle (Office). Selects the daytime shade
          model owned by ha-blinds: Sun tracking = geometric
          sun-tracking (workday default), Idle = the automation leaves
          the covers alone, opening them once and then holding off
          (weekend / public-holiday default). The options name the
          automation state, not where the covers sit, because since the
          2026-07-27 edge-driven model a manual change is designed to
          hold all day. ha-blinds calls this model "rest open" in its
          own docs; the divergence is deliberate. ha-blinds resets the
          helper to the calendar default at 00:05, so a flip here lasts
          the rest of the day; the position watchdog moves the covers
          within ~1 min. Renders only when the room has a live
          sunTracking overlay. */}
      {room.sunTracking != null && (
        <Card T={T} eyebrow="Automation">
          <Segmented T={T} ariaLabel="Blinds automation" height={48}
            options={[{ value: 'on', label: 'Sun tracking' }, { value: 'off', label: 'Idle' }]}
            value={room.sunTracking} onChange={(v) => update(room.id, { sunTracking: v })}/>
          <div style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.inkSoft }}>
            Resets to the calendar default at midnight: sun tracking on workdays, idle on weekends and public holidays.
          </div>
        </Card>
      )}
    </div>
  );
}

// AMediaModal removed 2026-05-25 (PR T3). TVShell + MusicShell
// replace its Apple TV remote + music functionality respectively.
// Last revision in git: parent of this commit.

// Self-contained thumbnail tile. Uses the blob-URL workaround for HA
// media_player_proxy images (iOS Safari rejects octet-stream); falls
// back to procedural CoverArt when the fetch fails or no URL is set.
function extractSpotifyRef(contentId) {
  if (!contentId) return null;
  const m = /spotify(?:%3a|:)(track|album|playlist)(?:%3a|:)([a-zA-Z0-9]+)/.exec(contentId);
  return m ? { type: m[1], id: m[2] } : null;
}

const SPOTIFY_THUMB_CACHE = new Map();   // key: `${type}:${id}` → url|null

function useSpotifyThumbnail(contentId) {
  const ref = extractSpotifyRef(contentId);
  const key = ref ? `${ref.type}:${ref.id}` : null;
  const [url, setUrl] = React.useState(() => (key && SPOTIFY_THUMB_CACHE.has(key)) ? SPOTIFY_THUMB_CACHE.get(key) : null);
  React.useEffect(() => {
    if (!ref) { setUrl(null); return; }
    if (SPOTIFY_THUMB_CACHE.has(key)) { setUrl(SPOTIFY_THUMB_CACHE.get(key)); return; }
    let cancelled = false;
    fetch(`https://open.spotify.com/oembed?url=https://open.spotify.com/${ref.type}/${ref.id}`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(j => {
        const thumb = j?.thumbnail_url || null;
        SPOTIFY_THUMB_CACHE.set(key, thumb);
        if (!cancelled) setUrl(thumb);
      })
      .catch(() => {
        SPOTIFY_THUMB_CACHE.set(key, null);   // remember failure so we don't keep retrying
        if (!cancelled) setUrl(null);
      });
    return () => { cancelled = true; };
  }, [key]);
  return url;
}

// Queue-row thumbnail: tries Spotify oembed for Spotify URIs, falls
// back to procedural CoverArt for everything else (or when oembed
// fails / is still loading).
function QueueThumbnail({ contentId, seed, size, radius = 4 }) {
  const url = useSpotifyThumbnail(contentId);
  // Numeric size → set width/height attrs directly. String size (e.g.
  // '100%' for fill-container layouts) → drive via style and pass a
  // sensible numeric fallback to CoverArt (whose initials fontSize is
  // size * 0.34 and breaks on strings).
  const numericFallback = typeof size === 'number' ? size : 240;
  if (url) {
    if (typeof size === 'number') {
      return <img src={url} alt="" width={size} height={size}
        style={{ borderRadius: radius, objectFit: 'cover', flexShrink: 0, display: 'block' }}/>;
    }
    return <img src={url} alt=""
      style={{ width: size, height: size, borderRadius: radius, objectFit: 'cover', flexShrink: 0, display: 'block' }}/>;
  }
  // For fill-container CoverArt we wrap so it stretches; the inner
  // CoverArt uses its numeric size for fontSize calcs, then 100% width
  // CSS on the wrapper makes the visual fill the parent.
  if (typeof size !== 'number') {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex' }}>
        <div style={{ width: '100%', height: '100%' }}>
          <CoverArt seed={seed} size={numericFallback} radius={radius}
            style={{ width: '100%', height: '100%' }}/>
        </div>
      </div>
    );
  }
  return <CoverArt seed={seed} size={size} radius={radius}/>;
}

// Heuristic: pick the favourites child out of a Sonos browse_media
// root. The Sonos integration usually exposes one of "Sonos Favorites",
// "Favorites", or "Favourites" — match on a substring of the title.
// Scenes are not wired to HA — see the SCENES note in RoomView. Same
// treatment here: inert, and honest about it rather than pretending the
// first entry is active.
function AScenesModal({ T, room }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
      {room.scenes.map((sc) => (
        <button key={sc} disabled title="Scenes aren't wired up yet" style={{
          padding: '20px 14px', border: `1px dashed ${T.hair}`,
          background: 'transparent',
          color: T.mute,
          fontFamily: A_FONT.serif, fontStyle: 'italic', fontSize: 18,
          borderRadius: 10, cursor: 'default', textAlign: 'left', fontWeight: 400,
        }}>{sc}</button>
      ))}
    </div>
  );
}

// Live scenes — rooms whose MAPPINGS carry `scenes` (Office, 2026-10-29:
// the ha-office-studio modes). Unlike the activity verbs, the state
// behind these is honest, so the current scene is drawn chosen. A press
// is busy until the state reads the pressed scene, floored at
// BUSY_MIN_VISIBLE so a re-press of the current one (which re-applies
// in HA but changes no state) still shows it registered.
const SCENE_INTENT_TIMEOUT = 10000;
function SceneRow({ T, room, scenes, runScene }) {
  const [pending, setPending] = React.useState(null);
  const [pendingAt, setPendingAt] = React.useState(0);
  React.useEffect(() => {
    if (pending == null) return;
    const settled = scenes.active === pending;
    const left = settled
      ? BUSY_MIN_VISIBLE - (Date.now() - pendingAt)
      : SCENE_INTENT_TIMEOUT - (Date.now() - pendingAt);
    const id = setTimeout(() => setPending(null), Math.max(0, left));
    return () => clearTimeout(id);
  }, [pending, pendingAt, scenes.active]);
  const press = (id) => {
    setPending(id);
    setPendingAt(Date.now());
    runScene(room.id, id);
  };
  return (
    <Segmented T={T} ariaLabel="Scenes" stretch={false} height={44}
      options={scenes.items.map((sc) => ({ value: sc.id, label: sc.label }))}
      value={scenes.active} busyValue={pending} onChange={press}/>
  );
}

// ── Main panel ───────────────────────────────────────────────────────────────

// Activity row — coordinated AV verbs for rooms with full TV/amp/Sonos
// stacks (Office, Pavilion, Rumpus). Each verb dispatches to a
// `script.*` (or `input_boolean.*` for extend toggles) via act(). The
// `idle` indicator reads binary_sensor.<room>_av_idle: true means TV
// off + Apple TV off/standby + Sonos not playing. Sonos-only rooms
// (Master Bed, Ensuite, Pool) have no `av` and render nothing here.
//
// `avActive` is the settle-truth the caller derives (room.idle when the
// room publishes an av_idle sensor, else the Screen tile's own awake
// read — Office has no binary_sensor.office_av_idle). It's what clears
// the busy affordance below.
function ActivityRow({ T, room, act, avActive }) {
  // Same in-flight treatment as the Screen tile's power toggle: these
  // verbs fire the *same* multi-second scripts, and until 2026-07-19
  // they had no feedback at all — a press changed nothing on screen
  // until the amp and TV caught up. Only one verb can be in flight at
  // a time (they're mutually exclusive), so a single slot suffices.
  const [pending, setPending] = React.useState(null);         // null | verb id
  const [pendingAt, setPendingAt] = React.useState(0);
  const pendingTimer = React.useRef(null);
  const clearPending = React.useCallback(() => {
    setPending(null);
    if (pendingTimer.current) { clearTimeout(pendingTimer.current); pendingTimer.current = null; }
  }, []);
  React.useEffect(() => {
    if (pending == null || avActive == null) return;
    // 'off' settles when the room goes quiet; watch/listen when it wakes.
    if (pending === 'off' ? avActive : !avActive) return;
    // Floored like the Screen tile's: a room that settles instantly
    // still has to show that the press registered.
    const left = BUSY_MIN_VISIBLE - (Date.now() - pendingAt);
    if (left <= 0) { clearPending(); return; }
    const id = setTimeout(clearPending, left);
    return () => clearTimeout(id);
  }, [pending, pendingAt, avActive, clearPending]);
  React.useEffect(() => () => { if (pendingTimer.current) clearTimeout(pendingTimer.current); }, []);
  const [, setPendingTick] = React.useState(0);
  React.useEffect(() => {
    if (pending == null) return;
    const id = setInterval(() => setPendingTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [pending]);
  const pendingSlow = pending != null && pendingAt > 0 && Date.now() - pendingAt > AV_INTENT_SLOW;
  const press = (verb) => {
    setPending(verb);
    setPendingAt(Date.now());
    setPendingTick(0);
    if (pendingTimer.current) clearTimeout(pendingTimer.current);
    pendingTimer.current = setTimeout(() => setPending(null), AV_INTENT_TIMEOUT);
    act(room.id, verb);
  };

  const av = room.av;
  if (!av) return null;
  const verbs = [
    av.watch  && { id: 'watch',  label: 'Watch',  busyLabel: 'Starting'   },
    av.listen && { id: 'listen', label: 'Listen', busyLabel: 'Starting'   },
    av.off    && { id: 'off',    label: 'Off',    busyLabel: 'Turning off' },
  ].filter(Boolean);
  if (verbs.length === 0) return null;

  // `room.idle`: true = idle, false = active, null = unknown.
  const idleBadge = room.idle == null ? null
    : room.idle ? { label: 'Idle',   color: T.mute  }
                : { label: 'Active', color: T.live };

  // One quiet tray of verbs. These are actions that start scripts, not
  // settings, and nothing reports which one the room is "in", so none is
  // drawn as selected; the one in flight lifts out of the tray as a key
  // with a spinner and says what it is doing.
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <div role="group" aria-label="Activity" style={{
        display: 'flex', padding: 4, borderRadius: RADIUS.control, background: T.cardSoft,
      }}>
        {verbs.map(v => {
          const busy = pending === v.id;
          return (
            <button key={v.id} onClick={() => press(v.id)} aria-busy={busy || undefined} style={{
              minWidth: 96, height: 44, padding: '0 18px', flexShrink: 0,
              border: 'none', borderRadius: RADIUS.key,
              background: busy ? T.keyFill : 'transparent',
              boxShadow: busy ? T.keyShadow : 'none',
              color: T.ink, cursor: 'pointer',
              fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              transition: 'background .2s, box-shadow .2s',
            }}>
              {busy && <BusyDot T={T} size={16}/>}
              {busy ? `${v.busyLabel}${pendingSlow ? '… still working' : '…'}` : v.label}
            </button>
          );
        })}
      </div>
      {idleBadge && (
        <span style={{
          fontFamily: A_FONT.sans, fontSize: fs(T, 13), fontWeight: 500,
          color: idleBadge.color, letterSpacing: '.12em', textTransform: 'uppercase',
        }}>{idleBadge.label}</span>
      )}
    </div>
  );
}

// RoomHero — the room's own photograph across the top of its screen,
// with the room's name set over it and a three-item summary at the
// right (inside temperature, lights on, what's playing). The menu, home
// and clock sit on the photo too, as plain light glyphs on a darkened
// top edge rather than as chips floating over the architecture. Kept to
// ~230px so the first row of tiles stays on an iPad's screen.
//
// At night a room with no night photograph (all of them, for now) shows
// its day photograph dimmed, the same fallback the home cards use.
// `photoFocus` (shared.jsx) says where to crop each room's photo.
// "18° showers" for the room header: the station reading and today's
// BOM summary. From 5 pm the summary is tomorrow's, which reads badly in
// a glance line ("18° tomorrow shower or two"), so it is left to the home
// card and the line shows the temperature alone. Either half is left out
// when BOM has nothing current; never a placeholder.
function weatherLine(hass, now) {
  const states = hass?.states;
  const t = readCurrent(states, now).temp;
  const s = summarise(readDays(states, now), now);
  const phrase = s && s.when === 'Today' ? asPhrase(s.short) : null;
  return [t != null ? `${Math.round(t)}°` : null, phrase].filter(Boolean).join(' ');
}

function RoomHero({ T, room, now, viewport, onMenuClick, onHome, summary, hass }) {
  const phone = viewport === 'phone';
  const height = phone ? 220 : viewport === 'tablet' ? 220 : 230;
  const photo = T.isDark ? (room.photoNight || room.photoDay) : room.photoDay;
  const dayAtNight = T.isDark && !room.photoNight;
  const glyphBtn = {
    width: 44, height: 44, border: 'none', background: 'transparent', padding: 0,
    color: T.onPhoto, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  return (
    <header style={{ position: 'relative', height, overflow: 'hidden', background: T.ink, color: T.onPhoto }}>
      {photo && (
        <img src={photo} alt="" style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          objectFit: 'cover', objectPosition: room.photoFocus || 'center',
          filter: dayAtNight ? 'brightness(.45) saturate(.75)' : 'none',
        }}/>
      )}
      <div aria-hidden style={{ position: 'absolute', inset: 0, background: T.heroScrimTop }}/>
      <div aria-hidden style={{ position: 'absolute', inset: 0, background: T.heroScrimBottom }}/>
      <div style={{
        position: 'absolute', top: 10, left: phone ? 6 : 26, right: phone ? 18 : 40,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', gap: 2 }}>
          <button onClick={onMenuClick} aria-label="Menu" style={glyphBtn}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M4 7h16M4 12h16M4 17h16"/></svg>
          </button>
          <button onClick={onHome} aria-label="Home" style={glyphBtn}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11l9-8 9 8"/><path d="M5 10v10h14V10"/></svg>
          </button>
        </div>
        <div style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
          {[fmtTime(now), weatherLine(hass, now)].filter(Boolean).join(' · ')}
        </div>
      </div>
      <div style={{
        position: 'absolute', left: phone ? 18 : 40, right: phone ? 18 : 40, bottom: phone ? 18 : 24,
        display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end',
        columnGap: 24, rowGap: 10,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 13), fontWeight: 500, letterSpacing: '.12em', textTransform: 'uppercase' }}>
            {greeting(now)}
          </div>
          <h1 style={{
            margin: 0, fontFamily: A_FONT.serif, fontWeight: 300,
            fontSize: phone ? 48 : viewport === 'tablet' ? 64 : 76,
            lineHeight: .9, letterSpacing: '-.02em',
          }}>{room.name}</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: phone ? 20 : 32, paddingBottom: phone ? 0 : 8 }}>
          {summary.map((it) => (
            <span key={it.key} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
              {it.glow && (
                <span aria-hidden style={{
                  width: 8, height: 8, borderRadius: '50%', alignSelf: 'center',
                  background: T.litFill, boxShadow: `0 0 10px 3px ${T.litSoft}`,
                }}/>
              )}
              {it.num != null && (
                <span style={{
                  fontFamily: A_FONT.serif, fontWeight: 500, fontSize: 32,
                  fontVariantNumeric: 'lining-nums tabular-nums',
                }}>
                  {it.num}
                  {/* Cormorant's degree sign is nearly a full-size letter o;
                      the sans one, set small and raised, reads as a degree. */}
                  {it.deg && <span style={{ fontFamily: A_FONT.sans, fontWeight: 400, fontSize: 14, verticalAlign: 12, marginLeft: -1 }}>°</span>}
                </span>
              )}
              <span style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 14) }}>{it.label}</span>
            </span>
          ))}
        </div>
      </div>
    </header>
  );
}

function RoomView({ T, room, update, updateLight, updateUtility, act, runScene, sendRemoteCommand, sonosCommand, sonosSpeakerCommand,
                    appleTvCommand, appleTvText, audioCommand,
                    togglePoolExtension, poolExtension, mediaPlayer, louvreCommand, destinationsCommand, coverCommand,
                    browseSonos, playMedia, getSonosQueue, speakers,
                    onMenuClick, onHome, onOpenMusic, onOpenTV, onOpenBuilder,
                    onOpenLights, onOpenShades, onOpenClimate, onOpenVacuum, hass, viewport }) {
  const active = room;
  const now = useClock();

  const lights = active.lights || [];
  const onCount = lights.filter(l => l.on).length;
  const tv = active.tv;
  const appleTv = active.appleTv;                                          // live; null when no Apple TV mapped
  const s = active.sonos;
  // Live queue + queue-item fallback. HA's Sonos integration sometimes
  // drops media_title / media_artist / entity_picture from the entity
  // attributes mid-track (Spotify Connect handoffs, etc.) while still
  // reporting state=playing. Pulling the current item from the queue
  // lets the tile and the modal show what's actually playing instead
  // of falling back to a generic "Playing" placeholder.
  const [sonosQueue, setSonosQueue] = React.useState([]);
  const [sonosRefreshTick, setSonosRefreshTick] = React.useState(0);
  React.useEffect(() => {
    if (!s) return;
    let cancelled = false;
    getSonosQueue?.(active.id).then((q) => {
      if (!cancelled) setSonosQueue(Array.isArray(q) ? q : []);
    });
    return () => { cancelled = true; };
  }, [active.id, s?.queuePosition, sonosRefreshTick, getSonosQueue, !!s]);
  React.useEffect(() => {
    if (!s) return;
    const id = setInterval(() => setSonosRefreshTick((t) => t + 1), 10000);
    return () => clearInterval(id);
  }, [!!s]);
  const sonosQueueItem = s ? sonosQueue[s.queuePosition ?? -1] : null;
  const sonosCoverUrl = useImageBlobUrl(s?.mediaImage);                    // null while loading / on error → CoverArt fallback
  const appleTvCoverUrl = useImageBlobUrl(appleTv?.mediaImage);            // real entity_picture when set, else null → the room photo

  // Apple-TV live scrubber: tick locally between HA pushes, mirroring the
  // Sonos modal pattern. Only runs while playing.
  const appleTvPlaying = appleTv?.state === 'playing';
  const appleTvDuration = appleTv?.mediaDuration || 0;
  const appleTvHasProgress = !!appleTv && appleTv.mediaPosition != null && appleTvDuration > 0;
  const [tickNow, setTickNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!appleTvPlaying) return;
    const id = setInterval(() => setTickNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [appleTvPlaying]);
  const appleTvLivePosition = (() => {
    if (!appleTv || appleTv.mediaPosition == null) return 0;
    if (!appleTvPlaying || !appleTv.mediaPositionUpdatedAt) return appleTv.mediaPosition;
    const updated = new Date(appleTv.mediaPositionUpdatedAt).getTime();
    const offset = Math.max(0, (tickNow - updated) / 1000);
    return appleTvDuration > 0
      ? Math.min(appleTv.mediaPosition + offset, appleTvDuration)
      : appleTv.mediaPosition + offset;
  })();
  const fmtMSS = (sec) => {
    if (sec == null || !isFinite(sec) || sec < 0) sec = 0;
    return `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, '0')}`;
  };

  // Whole-stack power intent. "Awake" is broader than `tv.on` because
  // the Apple TV reports independently of the TV (it can be on while the
  // TV is unavailable, mid-WoL, etc.), so the room looks asleep iff
  // *everything* in the AV stack is asleep.
  //
  // The intent tracking, the 40 s abandon window, the "still working"
  // degradation and the assumed_state settle problem all live in
  // useScreenPower — shared with the TV takeover's Power button so the
  // two controls that fire the same scripts behave the same way.
  const {
    awake: screenAwake, settled: screenSettled, busy: screenTransitioning,
    intent: screenIntent, slow: screenIntentSlow, press: pressScreenPower,
    label: screenPowerLabel,
  } = useScreenPower({ room: active, act, update });

  // Compose what to show as "now playing" on the Screen tile. Apple TV
  // gives the richest info (it reports media even when the BRAVIA is
  // mid-wake), so we prefer its title/app; fall back to the BRAVIA's
  // app_name, then to a generic label.
  //
  // In flight, the title says so outright. This is the signal that
  // actually answers "did my press register?" — the animated ring
  // catches the eye, the words confirm it, and without them the tile
  // still read "Standby" for the whole wake chain.
  const screenNow = (() => {
    if (screenTransitioning) return {
      title: screenIntent === 'on' ? 'Turning on…' : 'Turning off…',
      sub: screenIntentSlow ? 'still working' : (active.av ? 'TV · Apple TV · Amp' : 'Screen'),
      italic: true,
    };
    if (!screenAwake) return { title: 'Standby', sub: appleTv ? 'Apple TV' : 'Screen', italic: true };
    // Title / app only while the Apple TV is playing or paused: an idle
    // Apple TV keeps a stale app_name, and a deaf session sits at idle
    // while content plays (same rule as the TV view's strip). Idle claims
    // neither an app nor "Home", since it cannot tell the two apart.
    const atvActive = appleTv?.state === 'playing' || appleTv?.state === 'paused';
    if (atvActive && appleTv.title) return { title: appleTv.title, sub: appleTv.artist || appleTv.app || null };
    if (atvActive && appleTv.app)   return { title: appleTv.app,   sub: null };
    if (appleTv)        return { title: 'Apple TV',    sub: null, italic: true };
    if (tv?.app) {
      const [primary, ...rest] = tv.app.split(' · ');
      return { title: primary, sub: rest.length ? rest.join(' · ') : null };
    }
    return { title: 'TV', sub: null };
  })();

  // Source label: prefer the BRAVIA's reported source (when it's not
  // the placeholder "—"); else "Apple TV" when an Apple TV is mapped;
  // else just "Screen".
  // Screen-tile eyebrow. The TV brand mark (LG / BRAVIA) now lives
  // INSIDE TVShell on the now-playing strip — per user feedback
  // 2026-05-25 it belongs adjacent to the Apple TV cover/title, not
  // duplicated on the room tile. Tile label stays simple: current
  // source or "Apple TV".
  const screenLabel = (() => {
    if (tv?.source && tv.source !== '—') return `Screen · ${tv.source}`;
    if (appleTv) return 'Screen · Apple TV';
    return 'Screen';
  })();
  // Climate setpoint/mode awaiting HA's acknowledgement. Same delayed
  // reveal as everywhere else — see BUSY_REVEAL_DELAY.
  const climateBusy = useDelayedFlag(!!active.climate?.pending);
  const hasClimate = active.target != null;
  const hasShades = active.blinds != null || active.louvre != null || active.sheer != null || (active.louvres && active.louvres.length > 0);
  const hasLights = lights.length > 0;
  const hasScenes = !!active.liveScenes || (active.scenes && active.scenes.length > 0);
  const hasSonos = !!s;
  // Hero summary: what a passer-by needs, as large numerals.
  const summary = [
    hasClimate && { key: 'temp', num: Math.round(active.temp), deg: true, label: 'inside' },
    hasLights && { key: 'lights', num: onCount, label: onCount === 1 ? 'light on' : 'lights on', glow: onCount > 0 },
    { key: 'media', label: s?.playing ? 'Sonos playing' : tvIsOn(tv) ? 'Screen on' : 'Quiet' },
  ].filter(Boolean);

  const cols = viewport === 'phone' ? 1 : viewport === 'tablet' ? 2 : 4;
  const padding = viewport === 'phone' ? '16px 18px 28px' : viewport === 'tablet' ? '20px 26px 28px' : '24px 40px 40px';

  return (
    <div style={{
      width: '100%', minHeight: '100%',
      background: T.paper, color: T.ink,
      fontFamily: A_FONT.sans, position: 'relative',
    }}>
      <style>{`
        @keyframes a-fade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes a-pop  { from { opacity: 0; transform: translateY(8px) scale(.98) } to { opacity: 1; transform: none } }
        @keyframes a-eq   { from { height: 2px } to { height: 10px } }
        @keyframes a-ripple { from { transform: scale(.4); opacity: .6 } to { transform: scale(3); opacity: 0 } }
        @keyframes a-pulse  { 0%, 100% { opacity: .45 } 50% { opacity: 1 } }
        /* Touchpad long-press ring (tv/shell.jsx). Sweeps a 20px-radius
           circle, circumference 2*pi*20 = 125.7, so the dash offset runs
           126 to 0. Its duration is the panel's own HOLD_MS window, not
           anything the Apple TV reports: hold_secs sends one held press
           and returns, so there is nothing on the device to mirror. */
        @keyframes a-hold   { from { stroke-dashoffset: 126 } to { stroke-dashoffset: 0 } }
        /* In-flight ("busy") vocabulary — see PowerButton for why an
           opacity fade alone wasn't enough. a-spin drives a sweeping
           arc, a-halo pings a ring outward past the control's edge. */
        @keyframes a-spin   { to { transform: rotate(360deg) } }
        /* Halo caps at 1.28, not 1.5: the Screen tile's 57px button sits
           16px inside an overflow:hidden ATile, and a bigger ping gets
           its right edge sliced off. 1.28 → ~11px overhang, which clears. */
        @keyframes a-halo   { 0% { transform: scale(1); opacity: .55 } 75%, 100% { transform: scale(1.28); opacity: 0 } }
        /* Universal tap feedback for all buttons in the panel. Pressed
           state momentarily shrinks + dims. Touch devices get the same
           visual the mouse does (replaces the default greyish iOS
           highlight, which clashes with the Atelier palette). */
        button { transition: transform .1s ease, opacity .12s ease; -webkit-tap-highlight-color: transparent; }
        button:not(:disabled):active { transform: scale(.96); opacity: .82; }
        [data-dragging] button:active { transform: none; opacity: 1; }
      `}</style>

      <RoomHero T={T} room={active} now={now} viewport={viewport}
        onMenuClick={onMenuClick} onHome={onHome} summary={summary} hass={hass}/>

      <main style={{ padding, display: 'flex', flexDirection: 'column', gap: viewport === 'phone' ? 16 : 24 }}>
        {(active.av || hasScenes) && (
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 40, rowGap: 14 }}>
            {/* avActive: prefer the room's av_idle sensor; Office doesn't
                publish one, so fall back to the Screen tile's *settle* read
                — not its awake read, which Office's assumed_state TV flips
                in the same event as the service call and which therefore
                cleared these verbs' busy dot before it could be seen. */}
            <ActivityRow T={T} room={active} act={act}
              avActive={active.idle != null ? !active.idle : screenSettled}/>

            {/* SCENES — live where the room maps them (Office: the
                ha-office-studio modes, see SceneRow). Everywhere else still
                a placeholder, not a control: the names are mock strings in
                shared.jsx and HA's only scene entities are four unrelated
                Yubii leftovers.

                The placeholders deliberately get NO press feedback: adding a
                spinner to a control that does nothing would make a dead
                affordance look alive, which is worse than the honest silence.
                They take the key shape the redesign gives scenes, but stay
                `disabled` and visibly dimmed, and none is ever drawn as the
                running one (the old `i === 0` styling claimed one always was). */}
            {hasScenes && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, overflowX: 'auto', padding: '4px 2px 8px' }}>
                <span style={{
                  fontFamily: A_FONT.sans, fontSize: fs(T, 13), fontWeight: 500, letterSpacing: '.12em',
                  textTransform: 'uppercase', color: T.inkSoft, marginRight: 6, flexShrink: 0,
                }}>Scenes</span>
                {active.liveScenes ? (
                  <SceneRow T={T} room={active} scenes={active.liveScenes} runScene={runScene}/>
                ) : active.scenes.map((sc) => (
                  <button key={sc} disabled title="Scenes aren't wired up yet" style={{
                    height: 48, padding: '0 20px', flexShrink: 0,
                    border: 'none', borderRadius: RADIUS.control,
                    background: T.keyFill, boxShadow: T.keyShadow, color: T.ink, opacity: .5,
                    fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500, cursor: 'default',
                  }}>{sc}</button>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gridAutoRows: 'auto',
          gap: viewport === 'phone' ? 10 : 14,
        }}>
          {/* SCREEN — compact single-row: cover + title + meta + power
              toggle. Transport, scrubber, source picker, app launchers,
              and the full remote all live in the media modal. */}
          {tv && (() => {
            const meta = (() => {
              // Transitioning wins: screenAwake is still false during a
              // wake, and the early return below would swallow the
              // "TV · Apple TV · Amp" / "still working" sub-line.
              if (screenTransitioning) return screenNow.sub;
              if (!screenAwake) return null;
              const kbHint = active.keyboardFocus ? '⌨ waiting for text' : null;
              if (screenNow.italic) return kbHint || screenNow.sub;   // standby / idle: sub line carries "Apple TV" / "Home"
              const bits = [];
              if (kbHint) bits.push(kbHint);                          // surface first so the user sees it
              if (screenNow.sub) bits.push(screenNow.sub);
              if (appleTvPlaying) {
                bits.push('▮ playing');
                if (appleTvHasProgress) bits.push(`${fmtMSS(appleTvLivePosition)} / ${fmtMSS(appleTvDuration)}`);
              } else if (appleTv?.state === 'paused') {
                bits.push('paused');
              }
              return bits.length ? bits.join(' · ') : null;
            })();
            return (
              <ATile T={T} label={screenLabel} icon={I.tv}
                onTileClick={() => onOpenTV?.(active.id)}
                style={{ order: 1 }}
                headerAction={
                  // Power moved to the header row top-right (was at the
                  // bottom). Frees the body for a full-width square cover
                  // + title underneath, per user feedback 2026-05-24.
                  // PowerButton stops propagation itself, so the press
                  // doesn't bubble to the whole-tile-click and open the
                  // remote. 48×48 is the room-tile header size.
                  <PowerButton T={T} size={48}
                    on={screenAwake} busy={screenTransitioning}
                    onClick={pressScreenPower}
                    ariaLabel={screenPowerLabel}/>
                }>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
                  {/* Picture — full-width 16:10 via padding-bottom
                      (NOT aspect-ratio CSS, which has propagation bugs
                      with iPad Safari + CSS Grid row-sizing that
                      caused the cover to bleed past the tile boundary
                      into the tile below). Inner img fills the wrapper
                      absolutely. With no artwork it shows a quiet crop
                      of the room's own photo rather than the Apple TV
                      logo, which made a vendor's mark the heaviest
                      thing on the screen. */}
                  <div style={{ position: 'relative', width: '100%', paddingBottom: '62.5%' }}>
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', borderRadius: RADIUS.key, overflow: 'hidden', background: T.cardSoft }}>
                      {appleTvCoverUrl
                        ? <img src={appleTvCoverUrl} alt=""
                               style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}/>
                        : active.photoDay
                          ? <img src={active.photoDay} alt=""
                                 style={{
                                   width: '100%', height: '100%', objectFit: 'cover', display: 'block',
                                   filter: T.isDark ? 'brightness(.45) saturate(.5)' : 'saturate(.55) brightness(.92)',
                                 }}/>
                          : null}
                    </div>
                  </div>
                  {/* Title + subtitle — below the cover, occupying the
                      space that used to be empty real estate beside it. */}
                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      fontFamily: A_FONT.serif, fontSize: 24, lineHeight: 1.1, fontWeight: 500,
                      color: screenNow.italic ? T.inkSoft : T.ink,
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    }}>
                      {screenNow.title}
                    </div>
                    {meta && (
                      <div style={{
                        fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.inkSoft, marginTop: 4,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {meta}
                      </div>
                    )}
                  </div>
                </div>
              </ATile>
            );
          })()}

          {/* SONOS — compact single-row: cover + track + meta + play/pause.
              Prev/next, volume slider, scrubber, grouping, search, and
              queue all live in the sonos modal. */}
          {s && (() => {
            // The Sonos integration sometimes loses media_title mid-track
            // (Spotify Connect handoffs, brief disconnects), leaving
            // s.track null while playback continues. Fall back to the
            // current queue item — it carries title/artist/content_id
            // we can use for both text and Spotify thumbnail lookup.
            // Last resort: generic "Playing" if even the queue is dark.
            const queueTitle  = sonosQueueItem?.media_title || null;
            const queueArtist = sonosQueueItem?.media_artist || null;
            const queueContentId = sonosQueueItem?.media_content_id || null;
            const trackTitle = s.track
              ? (s.track.includes('—') ? s.track.split('—')[1].trim() : s.track)
              : (queueTitle || (s.playing ? 'Playing' : 'Nothing playing'));
            const trackArtist = s.track
              ? (s.track.includes('—') ? s.track.split('—')[0].trim() : s.artist)
              : (queueArtist || null);
            const meta = (s.track || queueTitle)
              ? [trackArtist, s.playing && '▮ playing', `${s.volume}%`].filter(Boolean).join(' · ')
              : (s.playing ? `▮ playing · ${s.volume}%` : 'Tap to open');
            const hasAnyMedia = !!(s.track || queueTitle);
            return (
              <ATile T={T} label={`Sonos · ${s.room}`} icon={I.speaker}
                onTileClick={() => onOpenMusic?.(active.id)}
                style={{ order: 2 }}
                headerAction={
                  // Play/Pause moved to the header row top-right, mirroring
                  // the screen tile. stopPropagation so the press doesn't
                  // bubble to the whole-tile-click and open MusicShell.
                  <button onClick={(e) => { e.stopPropagation(); sonosCommand(active.id, 'play_pause'); }}
                    style={{
                      width: 48, height: 48, borderRadius: '50%',
                      background: T.ink, color: T.paper, border: 'none', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}
                    aria-label={s.playing ? 'Pause' : 'Play'}>
                    {s.playing ? I.pause : I.play}
                  </button>
                }>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14, height: '100%' }}>
                  {/* Cover as a thumbnail beside the track: a full-width
                      square was the heaviest thing on the screen, and with
                      no artwork it was the Sonos logo. The fallback is now
                      the Spotify thumbnail, then a generated cover. */}
                  <div style={{ display: 'flex', gap: 14, alignItems: 'center', minWidth: 0 }}>
                    <div style={{ width: 64, height: 64, flexShrink: 0, borderRadius: RADIUS.key, overflow: 'hidden', background: T.cardSoft }}>
                      {sonosCoverUrl
                        ? <img src={sonosCoverUrl} alt="" width={64} height={64}
                               style={{ objectFit: 'cover', display: 'block' }}/>
                        : queueContentId
                          ? <QueueThumbnail contentId={queueContentId} seed={trackTitle} size={64} radius={0}/>
                          : <CoverArt seed={trackTitle || s.room || 'sonos'} size={64} radius={0}/>}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{
                        fontFamily: A_FONT.serif, fontSize: 24, fontWeight: 500, lineHeight: 1.05,
                        color: hasAnyMedia ? T.ink : T.inkSoft,
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      }}>
                        {trackTitle}
                      </div>
                      <div style={{
                        fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.inkSoft, marginTop: 4,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {meta}
                      </div>
                    </div>
                  </div>
                  {/* Build a playlist — opens the AI builder scoped to this
                      room (Claude → Spotify → play here via Sonos). */}
                  <button onClick={(e) => { e.stopPropagation(); onOpenBuilder?.(active.id); }}
                    style={{
                      marginTop: 'auto', height: 44,
                      background: T.cardSoft, border: 'none', borderRadius: RADIUS.control,
                      color: T.ink, fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
                      cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                    Build a playlist
                  </button>
                </div>
              </ATile>
            );
          })()}

          {/* LIGHTS — one key per fixture (see LightKey), grouped into
              Pavilion's open-plan zones where the lights carry a `zone`.
              Tap a key to switch it; tap a lit key's level to open the
              takeover for an exact level. There is deliberately no master
              brightness control (dropped on the 2026-10 redesign; scenes
              are the way to set a whole room at once). "All off" stays:
              one tap that darkens the room without hunting for which
              keys are lit. Lights flagged `available: false` are hidden. */}
          {hasLights && (() => {
            const visible = active.lights.filter(l => l.available !== false);
            const useZones = visible.some(l => l.zone);
            const ZONE_ORDER  = ['kitchen', 'living', 'dining', 'bedroom', 'wardrobe'];
            const ZONE_LABELS = { kitchen: 'Kitchen', living: 'Living', dining: 'Dining', bedroom: 'Bedroom', wardrobe: 'Wardrobe' };
            const groups = useZones
              ? ZONE_ORDER
                  .map(z => ({ key: z, label: ZONE_LABELS[z], lights: visible.filter(l => l.zone === z) }))
                  .filter(g => g.lights.length > 0)
                  .concat(
                    (() => {
                      const stray = visible.filter(l => !l.zone || !ZONE_ORDER.includes(l.zone));
                      return stray.length ? [{ key: 'other', label: 'Other', lights: stray }] : [];
                    })()
                  )
              : [{ key: 'all', label: null, lights: visible }];
            const utils = (active.utilities || []).filter(u => u.available !== false);
            // Zoned keys drop the "Zone · " prefix; the area label carries it.
            const keyName = (l) => useZones && l.name.includes(' · ')
              ? l.name.split(' · ').slice(1).join(' · ')
              : l.name;
            const toggleOne = (l) => updateLight(active.id, l.id, {
              on: !l.on,
              brightness: !l.on && l.brightness === 0 ? 50 : l.brightness,
            });
            const allOff = () => active.lights.forEach(l =>
              updateLight(active.id, l.id, { on: false, brightness: l.brightness }));
            // Zoned rooms get two grid columns (areas side by side) where the
            // grid has them; everything else is one column of keys.
            const span = useZones && cols >= 2 ? 2 : 1;
            const columns = span === 2 ? Math.min(3, groups.length + (utils.length ? 1 : 0)) : 1;
            const areaLabel = (text) => (
              <div style={{
                fontFamily: A_FONT.sans, fontSize: fs(T, 13), fontWeight: 500,
                letterSpacing: '.12em', textTransform: 'uppercase', color: T.inkSoft,
                padding: '0 0 2px 2px',
              }}>{text}</div>
            );
            return (
              <ATile T={T} label="Lighting" icon={I.bulb} span={span}
                onTileClick={() => onOpenLights?.(active.id)}
                style={{ order: 3 }}
                headerAction={onCount > 0 ? (
                  <button onClick={allOff} style={{
                    height: 44, padding: '0 4px', border: 'none', background: 'transparent',
                    fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
                    color: T.inkSoft, cursor: 'pointer',
                  }}>All off</button>
                ) : null}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{
                    fontFamily: A_FONT.serif, fontSize: 44, lineHeight: 1, fontWeight: 500,
                    color: T.ink, fontVariantNumeric: 'lining-nums tabular-nums',
                  }}>{onCount}</span>
                  <span style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.inkSoft }}>
                    of {visible.length} on
                  </span>
                </div>
                <div onClick={(e) => e.stopPropagation()} style={{
                  marginTop: 16, display: 'grid',
                  gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: 16,
                }}>
                  {groups.map(g => (
                    <div key={g.key} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {g.label && areaLabel(g.label)}
                      {g.lights.map(l => (
                        <LightKey key={l.id} T={T} name={keyName(l)} label={l.name}
                          on={l.on} level={l.brightness} pending={l.pending}
                          onToggle={() => toggleOne(l)}
                          onLevel={() => onOpenLights?.(active.id)}
                          onSetLevel={(v) => updateLight(active.id, l.id, { brightness: v, on: true })}/>
                      ))}
                    </div>
                  ))}
                  {/* Utilities — switched non-fixture loads (exhaust fans,
                      heated towel rails): the same key, with no level. */}
                  {utils.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {areaLabel('Utilities')}
                      {utils.map(u => (
                        <LightKey key={u.id} T={T} name={u.name} label={u.name}
                          on={u.on} level={null} pending={u.pending}
                          onToggle={() => updateUtility?.(active.id, u.id, { on: !u.on })}/>
                      ))}
                    </div>
                  )}
                </div>
              </ATile>
            );
          })()}

          {/* SHADES — Open / Close / Stop buttons for percentage covers
              (blinds, sheers). Replaces the previous slider which fired
              set_cover_position continuously during drag and confused
              the motor. Open/Close run the cover to its endpoint; Stop
              interrupts mid-motion. Louvres remain binary Open/Closed
              pairs (Fibaro pulse switches, see LouvresList). */}
          {hasShades && <ATile T={T} label="Shades" icon={I.blinds}
            onTileClick={() => onOpenShades?.(active.id)}
            style={{ order: 4 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* `moving` mirrors the Shades takeover — the cover's own
                  opening/closing state, so a travelling blind reads as
                  travelling on the room tile too, without opening it. */}
              {[
                active.blinds != null && { k: 'blinds', cap: 'Blinds', val: active.blinds, moving: active.blindsMoving },
                active.sheer  != null && { k: 'sheer',  cap: 'Sheers', val: active.sheer,  moving: active.sheerMoving },
              ].filter(Boolean).map(x => (
                <div key={x.k}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10, gap: 8 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500, color: T.ink }}>
                      {x.cap}
                      {x.moving && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: fs(T, 13), fontWeight: 400, color: T.inkSoft }}>
                          <BusyDot T={T} size={13}/>
                          {x.moving === 'opening' ? 'Opening' : 'Closing'}
                        </span>
                      )}
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 5 }}>
                      <span style={{ fontFamily: A_FONT.serif, fontSize: 32, fontWeight: 500, lineHeight: 1, color: T.ink, fontVariantNumeric: 'lining-nums tabular-nums' }}>{x.val}%</span>
                      <span style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.inkSoft }}>open</span>
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {['open', 'close', 'stop'].map((action) => {
                      // While a cover travels, its direction shows the
                      // spinner and Stop lifts out as a key — during a
                      // 20-60 s throw the useful action is "stop it".
                      const isTravelDirection =
                        (action === 'open' && x.moving === 'opening') ||
                        (action === 'close' && x.moving === 'closing');
                      const emphasise = isTravelDirection || (action === 'stop' && !!x.moving);
                      return (
                        <button key={action} onClick={(e) => { e.stopPropagation(); coverCommand?.(active.id, x.k, action); }}
                          aria-busy={isTravelDirection || undefined}
                          style={{
                            flex: 1, height: 44, minWidth: 0,
                            border: 'none', borderRadius: RADIUS.control,
                            background: emphasise ? T.keyFill : T.cardSoft,
                            boxShadow: emphasise ? T.keyShadow : 'none',
                            color: T.ink, cursor: 'pointer',
                            fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            transition: 'background .2s, box-shadow .2s',
                          }}>
                          {isTravelDirection && <BusyDot T={T} size={13}/>}
                          {action === 'open' ? 'Open' : action === 'close' ? 'Close' : 'Stop'}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              {active.louvres && active.louvres.length > 0 && (
                <div onClick={(e) => e.stopPropagation()}>
                  <LouvresList T={T} louvres={active.louvres} louvreCommand={louvreCommand}
                    label={active.louvres.length > 1 ? 'Louvres' : null}/>
                </div>
              )}
              {/* Sun-tracking day toggle — compact twin of the Shades
                  takeover's Automation card (same binding, same
                  semantics; see AShadesModal). stopPropagation so the
                  pills don't also open the takeover. */}
              {active.sunTracking != null && (
                <div onClick={(e) => e.stopPropagation()}>
                  <ALabel T={T} style={{ marginBottom: 10 }}>Automation</ALabel>
                  <Segmented T={T} ariaLabel="Blinds automation" height={40}
                    options={[{ value: 'on', label: 'Sun tracking' }, { value: 'off', label: 'Idle' }]}
                    value={active.sunTracking} onChange={(v) => update(active.id, { sunTracking: v })}/>
                </div>
              )}
            </div>
          </ATile>}

          {/* CLIMATE */}
          {hasClimate && (() => {
            const m = active.mode;
            // CLIMATE_ICON resolves heat/cool/auto/off to the Atelier
            // glyph set. Fallback to thermo for future HA modes
            // (dry / fan_only / etc.) so adding modes is non-breaking.
            const modeIcon = CLIMATE_ICON[m] || I.thermo;
            const tempColor = m === 'heat' ? T.heat
                            : m === 'cool' ? T.cool
                            : m === 'off'  ? T.mute
                            : T.ink;
            // Icon-next-to-temp tinted to match the temp — the two
            // signals carry the same mode-by-colour, with the glyph
            // adding mode-by-shape for at-a-distance read. User
            // direction 2026-05-25: icon belongs by the temp, not
            // in the eyebrow, and shares its colour. Cloning lets us
            // size the 18-default Atelier glyph to ~30px so it pairs
            // with the larger temp.
            const heroIcon = React.cloneElement(modeIcon, { style: { width: 28, height: 28 } });
            // Label goes through CLIMATE_MODE_LABEL so the tile reads
            // "Climate · Auto" rather than the raw hvac_mode "heat_cool".
            // Unmapped modes fall through to the raw value, which still
            // beats a blank.
            return (
            <ATile T={T} label={`Climate · ${CLIMATE_MODE_LABEL[m] || m}`}
              onTileClick={() => onOpenClimate?.(active.id)}
              style={{ order: 5 }}>
              {/* No dial (decided on the redesign canvas): a large reading
                  you can see from the bench, whole degrees, with the mode
                  glyph beside it in the mode's colour. Tenths stay on the
                  target, which is what a press changes. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ color: tempColor, display: 'flex', transition: 'color .25s ease' }}>
                  {heroIcon}
                </span>
                <span style={{
                  fontFamily: A_FONT.serif, fontSize: 64, lineHeight: .95, fontWeight: 500,
                  color: tempColor, fontVariantNumeric: 'lining-nums tabular-nums',
                  transition: 'color .25s ease',
                }}>
                  {Math.round(active.temp)}
                  <span style={{ fontFamily: A_FONT.sans, fontWeight: 400, fontSize: 24, verticalAlign: 26, marginLeft: -2 }}>°</span>
                </span>
              </div>
              <div style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.inkSoft, marginTop: 6 }}>
                Inside · {active.humidity}% humidity
              </div>
              {m === 'off' ? (
                <div style={{ marginTop: 'auto', paddingTop: 18,
                  fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500, color: T.inkSoft }}>
                  Off
                </div>
              ) : (
                <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 18 }}
                     onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => update(active.id, { target: Math.max(16, active.target - 0.5) })}
                    aria-label="Lower target by 0.5°"
                    style={{ width: 48, height: 48, borderRadius: '50%', border: 'none', background: T.cardSoft, color: T.ink, cursor: 'pointer', fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 12), fontWeight: 500, letterSpacing: '.12em', textTransform: 'uppercase', color: T.inkSoft }}>Target</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: A_FONT.sans, fontSize: fs(T, 17), fontWeight: 600, color: T.ink, fontVariantNumeric: 'tabular-nums' }}>
                      {climateBusy && <BusyDot T={T} size={12}/>}
                      {active.target.toFixed(1)}°
                    </span>
                  </div>
                  <button onClick={() => update(active.id, { target: Math.min(28, active.target + 0.5) })}
                    aria-label="Raise target by 0.5°"
                    style={{ width: 48, height: 48, borderRadius: '50%', border: 'none', background: T.cardSoft, color: T.ink, cursor: 'pointer', fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                </div>
              )}
            </ATile>
            );
          })()}

          {/* CLIMATE, read-only — a room with temp and humidity sensors
              but no climate entity (Rumpus). Same reading as the tile
              above, without the controls or the tap-through. */}
          {!hasClimate && active.temp != null && active.humidity != null && (
            <ATile T={T} label="Climate" style={{ order: 5 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ color: T.ink, display: 'flex' }}>
                  {React.cloneElement(I.thermo, { style: { width: 28, height: 28 } })}
                </span>
                <span style={{
                  fontFamily: A_FONT.serif, fontSize: 64, lineHeight: .95, fontWeight: 500,
                  color: T.ink, fontVariantNumeric: 'lining-nums tabular-nums',
                }}>
                  {Math.round(active.temp)}
                  <span style={{ fontFamily: A_FONT.sans, fontWeight: 400, fontSize: 24, verticalAlign: 26, marginLeft: -2 }}>°</span>
                </span>
              </div>
              <div style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.inkSoft, marginTop: 6 }}>
                Inside · {Math.round(active.humidity)}% humidity
              </div>
            </ATile>
          )}

          {/* VACUUM — status-and-open, only on the rooms the robot can
              actually reach. Office, Master Bedroom and Master Ensuite
              are one area each; Pavilion is five, because the panel's
              rooms are amalgams of the robot's map (see vacuum/policy.js
              PANEL_ROOM_AREAS). Rumpus is downstairs and Pool is
              outdoors, so neither gets a tile — those are permanent
              exclusions, not gaps.

              Deliberately NOT one-tap dispatch. These are wall-mounted
              iPads that people brush past, and the thing on the other end
              drives itself into rooms. Tapping opens the shell with this
              room's areas preselected. What the tile earns its place with
              is state the drawer cannot show: whether the robot is
              working in THIS room right now. */}
          {(() => {
            const areas = areasForPanelRoom(active.id);
            if (!areas.length) return null;
            const vs = hass?.states || {};
            const status  = vs[VAC.status]?.state;
            const here    = vs[VAC.currentRoom]?.state;
            const battery = vs[VAC.battery]?.state;
            const running = isRunningSegment(status);
            const docked  = vs[VAC.entity]?.state === 'docked';
            const inThisRoom = running && areas.some((a) => areaName(a) === here);
            const line = inThisRoom
              ? (status.includes('mop') ? 'Mopping here' : 'Vacuuming here')
              : running
                ? `Busy · ${here && here !== 'unknown' ? here : 'elsewhere'}`
                : docked ? 'Docked' : 'Away from the dock';
            return (
              <ATile T={T} label="Vacuum"
                icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="3"/></svg>}
                onTileClick={() => onOpenVacuum?.(active.id)}
                style={{ order: 6 }}>
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <div style={{
                    fontFamily: A_FONT.serif, fontSize: 28, lineHeight: 1.1,
                    fontWeight: 500,
                    color: inThisRoom ? T.live : T.ink,
                    transition: 'color .25s ease',
                  }}>{line}</div>
                  <div style={{
                    fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.inkSoft, marginTop: 6,
                  }}>
                    {areas.length > 1 ? `${areas.length} rooms here` : areaName(areas[0])}
                  </div>
                  {docked && battery != null && battery !== 'unknown' && (
                    <div style={{
                      marginTop: 'auto', paddingTop: 18,
                      fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.inkSoft,
                    }}>{battery}% charged</div>
                  )}
                </div>
              </ATile>
            );
          })()}
        </div>
      </main>

      {/* All AModal mounts retired 2026-05-24/25. Climate / Lighting /
          Shades route to ClimateShell / LightsShell / ShadesShell;
          Media (Apple TV remote) routes to TVShell (PR T3); Sonos
          routes to MusicShell (ASonosModal retired 2026-05-25). */}
    </div>
  );
}

function IconBtn({ T, onClick, ariaLabel, children }) {
  return (
    <button aria-label={ariaLabel} onClick={onClick} style={{
      width: 44, height: 44, borderRadius: '50%', border: 'none',
      background: T.cardSoft, color: T.ink, cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>{children}</button>
  );
}

// ── TV brand mapping — used by tile + shell labels ──────────────────────────
//
// Per-room TV brand. Hardcoded because each TV is a stable physical
// fact, not something the panel should discover. Surfaces in the
// Screen-tile label + TVShell eyebrow so the user knows whether a
// remote command targets the LG / BRAVIA itself vs the Apple TV
// attached to it. Per user feedback 2026-05-25.
const TV_BRAND = {
  pavilion: 'LG',
  office:   'BRAVIA',
  rumpus:   'BRAVIA',
};
function tvBrandFor(roomId) {
  return TV_BRAND[roomId] || null;
}

// ── Card — surface for grouping controls inside a takeover shell ────────────
//
// Matches the room-tile (ATile) visual language so shell content reads
// as a continuation of the home view, just at a bigger surface. Header
// row carries the eyebrow label + optional right-side action button
// (typically a PowerButton acting as a zone-level master toggle).
function Card({ T, eyebrow, headerAction, children, style }) {
  return (
    <div style={{
      background: T.card, borderRadius: RADIUS.card, boxShadow: T.cardShadow,
      padding: '20px 24px', color: T.ink, fontFamily: A_FONT.sans,
      display: 'flex', flexDirection: 'column', gap: 14,
      ...style,
    }}>
      {(eyebrow || headerAction) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {eyebrow && <ALabel T={T}>{eyebrow}</ALabel>}
          {headerAction && (
            <div style={{ marginLeft: 'auto', display: 'flex' }}>
              {headerAction}
            </div>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

// PowerButton — extracted from the TV card so other surfaces (zone
// masters in LightsShell, etc.) can reuse the same affordance. `on`
// toggles the lit treatment; default 44×44 is the right size for
// an in-card master toggle (smaller than the TV card's 57×57 which
// sits in the room-tile header).
//
// `busy` is the in-flight affordance. The Office / Rumpus / Pavilion
// watch chains (TV WoL → amp source+mode → Apple TV wake) run 5–20 s,
// and until 2026-07-19 the only signal was an opacity fade between
// .45 and 1. User feedback: not obvious enough. It fails for two
// reasons — a dimming grey glyph on a transparent circle is nearly
// invisible on a sunlit iPad, and the button kept its *off* colours
// for the whole chain, so the primary "did that register?" read said
// no while the room was actually waking. People pressed again.
//
// Busy now stacks three signals that survive glare and peripheral
// vision, and it deliberately uses the busy colour (not the target
// state's colours) in both directions — busy means "something is
// happening here"; the tile copy says which direction:
//   1. the control adopts the busy treatment the instant it's pressed;
//   2. an arc sweeps the circumference — motion of a *shape*, which
//      reads far better than motion of opacity;
//   3. a halo pings outward past the button edge.
//
// `tone` says what the button powers. Lights take the lit-key oak when
// on (oak means a light is on and nothing else); everything else — a
// screen, a whole AV stack — takes joinery black, the primary fill.
function PowerButton({ T, on, busy = false, onClick, size = 44, ariaLabel = 'Power', tone = 'device' }) {
  const onFill = tone === 'light' ? T.litFill : T.primary;
  const onInk  = tone === 'light' ? T.litFillInk : T.primaryInk;
  return (
    <button onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      aria-label={ariaLabel}
      aria-busy={busy || undefined}
      style={{
        width: size, height: size, borderRadius: '50%',
        border: 'none',
        background: busy ? T.busySoft : on ? onFill : T.cardSoft,
        color: busy ? T.busy : on ? onInk : T.ink,
        cursor: 'pointer', padding: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background .2s, color .2s',
        position: 'relative',
      }}>
      {busy && <BusyRing T={T} size={size}/>}
      <span style={{ display: 'flex' }}>{I.power}</span>
    </button>
  );
}

// BusyDot — inline sibling of BusyRing for pill-shaped controls (the
// Activity verbs), where a ring overhanging the host would collide
// with the pill's border. Same sweeping-arc idiom, laid out in flow
// next to the label rather than stacked over it.
//
// The arc is longer than BusyRing's for a reason: a quarter-turn
// works at 57px and turns into an unreadable speck at 14px — on the
// first pass it read as a stray apostrophe beside the label. Small
// sizes need ~55% of the circumference plus a faint full-circle
// track before they resolve as a spinner.
function BusyDot({ T, size = 14, color }) {
  // `color` overrides the busy role where the dot sits on a coloured
  // fill (a lit light key) and the default would lose contrast.
  const c = color || T.busy;
  return (
    <svg aria-hidden width={size} height={size} viewBox="0 0 100 100"
      style={{ flexShrink: 0, animation: 'a-spin .9s linear infinite' }}>
      <circle cx="50" cy="50" r="40" fill="none"
        stroke={c} strokeWidth="16" opacity=".25"/>
      {/* r=40 → circumference ~251; 138/113 paints just over half. */}
      <circle cx="50" cy="50" r="40" fill="none"
        stroke={c} strokeWidth="16" strokeLinecap="round"
        strokeDasharray="138 113"/>
    </svg>
  );
}

// BusyRing — the sweeping arc + outward halo that sit on top of a
// circular control while its action is in flight. Absolutely
// positioned and pointer-events:none so it never eats the tap, and
// aria-hidden because `aria-busy` on the host button is what actually
// carries the state to assistive tech.
//
// Overhangs the host by 3px on each side, which is why every call site
// needs the host to be position:relative and NOT overflow:hidden.
function BusyRing({ T, size }) {
  return (
    <>
      <span aria-hidden style={{
        position: 'absolute', inset: -3, borderRadius: '50%',
        border: `2px solid ${T.busy}`, pointerEvents: 'none',
        animation: 'a-halo 1.6s ease-out infinite',
      }}/>
      {/* Faint full-circle track, so the sweeping arc reads as a
          progress ring rather than a stray crescent. Static — only the
          arc above it spins. */}
      <svg aria-hidden width={size + 6} height={size + 6} viewBox="0 0 100 100"
        style={{ position: 'absolute', top: -3, left: -3, pointerEvents: 'none' }}>
        <circle cx="50" cy="50" r="46" fill="none"
          stroke={T.busy} strokeWidth="6" opacity=".22"/>
      </svg>
      {/* r=46 → circumference ~289; dasharray 96/193 paints a third turn. */}
      <svg aria-hidden width={size + 6} height={size + 6} viewBox="0 0 100 100"
        style={{
          position: 'absolute', top: -3, left: -3, pointerEvents: 'none',
          animation: 'a-spin .9s linear infinite',
        }}>
        <circle cx="50" cy="50" r="46" fill="none"
          stroke={T.busy} strokeWidth="6" strokeLinecap="round"
          strokeDasharray="96 193"/>
      </svg>
    </>
  );
}

// ── TakeoverShell — shared full-takeover chrome ─────────────────────────────
//
// Used by every shell in the panel (Music, TV, Lights, Shades, Climate,
// GuestMode). Replaced the centered-AModal pattern across two phases
// (2026-05-24 for L/S/C, 2026-05-25 for the M/TV unification).
//
// Shape: z=60, paper background, back chevron + eyebrow + serif title
// in the header, optional headerAction slot on the right.
//
// Two escape hatches for the richer shells:
//   - `onBack`     — overrides the back chevron + ESC handler. Default
//                    is `onClose`. MusicShell passes its `goBack` so
//                    ESC and the chevron pop one view-mode (S2→S0 etc)
//                    rather than closing outright.
//   - `bodyless`   — skip the centered-padded body wrapper, render
//                    children directly inside the scroll container.
//                    MusicShell needs this because each view-mode
//                    handles its own padding + the now-playing hero
//                    can extend edge-to-edge.
function TakeoverShell({ T, viewport, eyebrow, title, onClose, onBack, headerAction, bodyless, children }) {
  const back = onBack || onClose;
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') back?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [back]);
  const isPhone = viewport === 'phone';
  const headerPad = isPhone ? { v1: 18, h: 18, v2: 14 } : { v1: 22, h: 36, v2: 14 };
  const bodyPad   = isPhone ? { v1: 18, h: 18, v2: 32 } : { v1: 24, h: 36, v2: 40 };
  return (
    <div style={{
      // iOS safe-area: the container goes edge-to-edge (inset: 0) and
      // paints `T.paper` across the entire viewport — including the
      // status-bar / Dynamic-Island / home-indicator strips — so the
      // underlying RoomView can't bleed through. Safe-area is applied
      // as *padding* on the header (top + sides) and body (sides +
      // bottom) so content still sits inside the iPhone's usable area.
      // Bodyless callers (music + tv shells) bring their own inner
      // padding and add `env(safe-area-inset-bottom)` themselves.
      position: 'fixed',
      inset: 0,
      zIndex: 60,
      background: T.paper, color: T.ink,
      overflowY: 'auto',
      fontFamily: A_FONT.sans,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 18,
        paddingTop:    `calc(${headerPad.v1}px + env(safe-area-inset-top))`,
        paddingRight:  `calc(${headerPad.h}px + env(safe-area-inset-right))`,
        paddingBottom: `${headerPad.v2}px`,
        paddingLeft:   `calc(${headerPad.h}px + env(safe-area-inset-left))`,
      }}>
        <button onClick={back} aria-label="Back" style={{
          width: 44, height: 44, borderRadius: '50%',
          border: 'none', background: T.cardSoft,
          color: T.ink, cursor: 'pointer', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 6l-6 6 6 6"/>
          </svg>
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <ALabel T={T}>{eyebrow}</ALabel>
          <div style={{
            fontFamily: A_FONT.serif, fontSize: isPhone ? 30 : 36,
            color: T.ink, lineHeight: 1, fontWeight: 500,
            letterSpacing: '-.01em', marginTop: 6,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {title}
          </div>
        </div>
        {headerAction}
      </div>
      {bodyless ? children : (
        <div style={{
          paddingTop:    `${bodyPad.v1}px`,
          paddingRight:  `calc(${bodyPad.h}px + env(safe-area-inset-right))`,
          paddingBottom: `calc(${bodyPad.v2}px + env(safe-area-inset-bottom))`,
          paddingLeft:   `calc(${bodyPad.h}px + env(safe-area-inset-left))`,
          maxWidth: 760, margin: '0 auto', width: '100%',
          boxSizing: 'border-box',
        }}>
          {children}
        </div>
      )}
    </div>
  );
}

export {
  RoomView, IconBtn, A_LIGHT, A_DARK, A_FONT, ALabel, useImageBlobUrl, fs,
  TakeoverShell, Card, tvBrandFor,
  // The 2026-10 control vocabulary, for shells outside this file.
  Segmented, LightKey,
  // Busy vocabulary — PowerButton's `busy` prop plus the two spinner
  // primitives, so any surface firing a slow script can show the same
  // in-flight affordance instead of inventing its own.
  PowerButton, BusyRing, BusyDot,
  // Modal bodies — exported so LightsShell / ShadesShell / ClimateShell
  // wrap them inside TakeoverShell without duplicating logic. All AModal
  // mounts have been retired (the AModal primitive itself was deleted
  // 2026-05-25 along with ASonosModal); these bodies live on as the
  // content-side reusable components.
  ALightsModal, AShadesModal, AClimateModal,
};
export default RoomView;
